import {
  AnimatePresence,
  MotionConfig,
  motion,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import {
  ArrowDown,
  ArrowRight,
  BadgeCheck,
  Bike,
  BookOpen,
  Boxes,
  Building2,
  Check,
  ChevronDown,
  ChevronLeft,
  CircleDollarSign,
  Clipboard,
  Clock3,
  Download,
  Factory,
  Gift,
  Globe2,
  GraduationCap,
  HandHeart,
  HeartHandshake,
  Home,
  Library,
  Menu,
  MessageCircleHeart,
  PackageCheck,
  Printer,
  Recycle,
  RefreshCw,
  Route,
  Search,
  ShieldCheck,
  Sparkles,
  Store,
  ToolCase,
  Users,
  Wrench,
  X,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from "react";
import { CommunityCampaignPage, CommunityDirectoryPage } from "./CommunityCampaignPage";
import { ConsentPanel } from "./ConsentPanel";
import { DeferredQRCode } from "./DeferredQRCode";
import { initGa4, trackWebsitePageView } from "./analytics";
import { resolveHeroVariant, trackHeroAction, trackHeroExposure } from "./hero-experiment";
import { recordPageViewOk, reliabilityFetch } from "./reliability";

const APP_URL = "https://app.greenswap.us";
const PUBLIC_SITE_URL = (import.meta.env.VITE_PUBLIC_SITE_URL ?? "https://greenswap.us").replace(/\/$/, "");
const IS_LOCAL_PREVIEW = window.location.hostname === "127.0.0.1" || window.location.hostname === "localhost";
const API_BASE_URL = (import.meta.env.VITE_APP_API_URL ?? (IS_LOCAL_PREVIEW ? "http://127.0.0.1:3001/api" : "/api")).replace(/\/$/, "");
const PARTNER_INQUIRY_EMAIL = "info@greenswap.us";
const MEMBER_PATH = "/members";
const SIGN_IN_URL = `${APP_URL}/login?returnTo=${encodeURIComponent(MEMBER_PATH)}`;
const REGISTER_URL = `${APP_URL}/register?returnTo=${encodeURIComponent(MEMBER_PATH)}`;
const BROWSE_URL = `${APP_URL}/browse`;

type PathKey = "trade" | "give" | "lend" | "donate" | "repair" | "recycle";
type GuideChoice = { id: string; label: string; description: string; icon: LucideIcon };

const categories: GuideChoice[] = [
  { id: "home", label: "Home & furniture", description: "Chairs, lamps, shelves, kitchenware", icon: Home },
  { id: "tools", label: "Tools & equipment", description: "Hand tools, garden gear, machines", icon: ToolCase },
  { id: "tech", label: "Electronics", description: "Devices, cables, small appliances", icon: Boxes },
  { id: "life", label: "Books, clothing & more", description: "Everyday useful things", icon: BookOpen },
];

const conditions: GuideChoice[] = [
  { id: "ready", label: "Ready for a new home", description: "Clean, working, and useful now", icon: Sparkles },
  { id: "care", label: "Needs a little care", description: "A tune-up or small fix would help", icon: Wrench },
  { id: "repair", label: "Needs real repair", description: "Useful, but not working as intended", icon: ToolCase },
  { id: "end", label: "At the end of its life", description: "Unsafe or beyond practical repair", icon: Recycle },
];

const priorities: GuideChoice[] = [
  { id: "near", label: "Keep it close", description: "A simple handoff in my community", icon: Route },
  { id: "fast", label: "Move it soon", description: "The least-friction useful outcome", icon: Clock3 },
  { id: "impact", label: "Help where it matters", description: "Match a real local need", icon: HandHeart },
  { id: "keep", label: "Keep ownership", description: "Let someone borrow it and return it", icon: RefreshCw },
];

const pathContent: Record<PathKey, { eyebrow: string; title: string; body: string; icon: LucideIcon; alternatives: string[] }> = {
  trade: {
    eyebrow: "Strong first route",
    title: "Trade it nearby",
    body: "It is ready to use and likely valuable to someone close. A local trade gives it momentum without turning the handoff into waste.",
    icon: RefreshCw,
    alternatives: ["Offer it free", "Donate to a matched organization"],
  },
  give: {
    eyebrow: "Fastest useful route",
    title: "Give it to a neighbor",
    body: "A direct local handoff is likely to be the quickest way to put this useful thing back to work.",
    icon: Gift,
    alternatives: ["Trade it", "Add it to a community wish list"],
  },
  lend: {
    eyebrow: "Access without replacement",
    title: "Lend it locally",
    body: "You can keep ownership while helping someone avoid buying something they only need for a short time.",
    icon: HeartHandshake,
    alternatives: ["Offer it to a tool library", "Trade it when you are ready"],
  },
  donate: {
    eyebrow: "Community-need route",
    title: "Match it to an organization",
    body: "A qualified donation starts with what an organization can actually accept, not a hopeful drop-off that creates more work.",
    icon: Building2,
    alternatives: ["Give it nearby", "Add it to a community wish list"],
  },
  repair: {
    eyebrow: "Life-extension route",
    title: "Repair it first",
    body: "Its next life may begin with care. GreenSwap’s roadmap connects repairable items with local skills, services, and evidence of the work completed.",
    icon: Wrench,
    alternatives: ["Find a repair café", "Offer it as a repair project"],
  },
  recycle: {
    eyebrow: "Last-resort route",
    title: "Find responsible end-of-life care",
    body: "If it cannot be used safely or repaired, route it to a verified local program. Recycling is the final option, not the automatic one.",
    icon: Recycle,
    alternatives: ["Confirm repair is impractical", "Locate an appropriate material stream"],
  },
};

const partnerProfiles = [
  {
    id: "libraries",
    label: "Tool libraries",
    inquiryRole: "Tool library",
    icon: Library,
    title: "Put shared inventory within reach.",
    body: "Help residents discover tools, borrow with confidence, return on time, and see the care history behind shared equipment.",
    outcomes: ["More useful checkouts", "Easier inventory discovery", "Maintenance and repair history"],
  },
  {
    id: "repair",
    label: "Repair groups",
    inquiryRole: "Repair organization",
    icon: Wrench,
    title: "Turn repair intent into completed care.",
    body: "Receive qualified referrals, show service areas, coordinate evidence, and return repaired items to circulation.",
    outcomes: ["Better-qualified referrals", "Volunteer visibility", "Verified repair outcomes"],
  },
  {
    id: "nonprofits",
    label: "Reuse nonprofits",
    inquiryRole: "Reuse nonprofit",
    icon: HandHeart,
    title: "Receive what you can truly use.",
    body: "Publish current needs and acceptance rules so donations arrive matched, useful, and easier for teams to process.",
    outcomes: ["Qualified donations", "Less intake friction", "New volunteer connections"],
  },
  {
    id: "public",
    label: "Cities & schools",
    inquiryRole: "City or school",
    icon: GraduationCap,
    title: "See the local reuse system without exposing people.",
    body: "Connect surplus, libraries, repair programs, and community partners through privacy-preserving program evidence.",
    outcomes: ["Surplus circulation", "Program-level learning", "Privacy-preserving reports"],
  },
];

const sponsorPrograms = [
  {
    id: "repair",
    label: "Repair access",
    icon: Wrench,
    title: "Help a community repair before it replaces.",
    description: "Underwrite partner capacity, repair events, parts, and access for residents.",
    proof: ["Completed repair events", "Partner-confirmed outcomes", "Methodology and assurance labels"],
  },
  {
    id: "tools",
    label: "Shared tools",
    icon: ToolCase,
    title: "Make occasional-use equipment available to more people.",
    description: "Support tool-library inventory, maintenance, and neighborhood discovery.",
    proof: ["Successful loans and returns", "Inventory utilization", "Access across service areas"],
  },
  {
    id: "surplus",
    label: "Employee surplus",
    icon: Building2,
    title: "Move useful workplace surplus into local circulation.",
    description: "Coordinate employee participation and responsible handoffs through verified partners.",
    proof: ["Custody-confirmed transfers", "Destination-aware reporting", "No double counting"],
  },
];

const roadmap = [
  { phase: "Now", title: "Neighbor-to-neighbor circulation", body: "Discover, list, trade, give, lend, and build trusted local communities.", status: "Available in the app" },
  { phase: "Next", title: "The Next Life Guide", body: "One starting point for trade, donation matching, borrowing, repair, and responsible end-of-life routes.", status: "Experience preview" },
  { phase: "Pilot", title: "Partner-powered services", body: "Verified organizations, shared inventory, lending, repair referrals, qualified donations, and sponsor-supported programs.", status: "Available to approved pilot partners" },
  { phase: "Future", title: "Community Circular OS", body: "Portable item histories, open interfaces, privacy-preserving evidence, and community-controlled federation.", status: "Long-term direction" },
];

function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function Reveal({ children, className = "", delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, y: 28 }}
      whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.18 }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function Brand() {
  return (
    <a className="brand" href="/" aria-label="GreenSwap home">
      <img className="brand-mark" src="/branding/greenswap-mark.svg" alt="" aria-hidden="true" draggable={false} />
      <span className="brand-wordmark" aria-hidden="true"><span>Green</span><span>Swap</span></span>
    </a>
  );
}

function Header({ onOpenInquiry }: { onOpenInquiry: (role?: string) => void }) {
  const [open, setOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const links = [
    ["How GreenSwap works", "/start"],
    ["Get involved", "/partners"],
    ["Fund a pilot", "/sponsors"],
    ["Impact", "/impact"],
  ];

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      window.requestAnimationFrame(() => menuButtonRef.current?.focus());
    };
    window.addEventListener("resize", close);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("resize", close);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  return (
    <header className="site-header">
      <div className="shell header-inner">
        <Brand />
        <nav className="desktop-nav" aria-label="Primary navigation">
          {links.map(([label, href]) => <a key={href} href={href}>{label}</a>)}
        </nav>
        <div className="header-actions">
          <a className="text-button desktop-auth-link" href={SIGN_IN_URL}>Sign in</a>
          <a className="button button-primary button-small desktop-auth-link" href={BROWSE_URL}>Explore GreenSwap <ArrowRight size={16} /></a>
          <button ref={menuButtonRef} className="menu-button" onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? "Close menu" : "Open menu"}>
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            id="mobile-menu"
            className="mobile-menu"
            aria-label="Mobile navigation"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
          >
            <div className="shell mobile-menu-inner">
              {links.map(([label, href], index) => (
                <a key={href} href={href} onClick={() => setOpen(false)}><span>0{index + 1}</span>{label}<ArrowRight /></a>
              ))}
              <div className="mobile-auth-actions" aria-label="GreenSwap account">
                <a href={SIGN_IN_URL} onClick={() => setOpen(false)}>Sign in</a>
                <a className="mobile-register-link" href={BROWSE_URL} onClick={() => setOpen(false)}>Explore GreenSwap <ArrowRight /></a>
              </div>
              <button onClick={() => { setOpen(false); onOpenInquiry(); }}>Start a pilot conversation <MessageCircleHeart /></button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}

function Hero() {
  const heroRef = useRef<HTMLElement>(null);
  const selection = useMemo(resolveHeroVariant, []);
  const variant = selection.variant;
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "12%"]);
  const copyY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "-8%"]);

  useEffect(() => {
    trackHeroExposure(variant.id, selection.preview);
  }, [selection.preview, variant.id]);

  return (
    <section className="hero" id="top" ref={heroRef} aria-labelledby="hero-title">
      <motion.div className="hero-image" style={{ y: imageY }} aria-hidden="true" />
      <div className="hero-scrim" aria-hidden="true" />
      <motion.div className="shell hero-inner" style={{ y: copyY }}>
        <p className="eyebrow eyebrow-light"><span>{variant.eyebrow}</span></p>
        <h1 id="hero-title" className={`hero-title hero-title--${variant.id}`}><span className="hero-title-line">{variant.titleLineOne}</span><span className="hero-title-line">{variant.id === "mission" ? <em>{variant.titleLineTwo}{"\u00a0"}{variant.titleEmphasis}</em> : <>{variant.titleLineTwo} <em>{variant.titleEmphasis}</em></>}</span></h1>
        <p className="hero-lede">{variant.lede}</p>
        <div className="hero-actions">
          <button className="button button-primary" onClick={() => { trackHeroAction(variant.id, "primary", selection.preview); scrollToId("home-recognition"); }}>{variant.primaryLabel} <ArrowDown size={18} /></button>
          <a className="button button-glass" href="/about" onClick={() => trackHeroAction(variant.id, "secondary", selection.preview)}>{variant.secondaryLabel}</a>
        </div>
        <p className="hero-proof"><BadgeCheck size={17} /> {variant.proof}</p>
      </motion.div>
      <div className="hero-caption" aria-hidden="true"><span>01</span><p>{variant.captionLineOne}<br />{variant.captionLineTwo}</p></div>
      <button className="scroll-cue" onClick={() => scrollToId("home-recognition")} aria-label="Scroll to see how GreenSwap works"><ArrowDown /></button>
    </section>
  );
}

function SectionIntro({ kicker, title, body, light = false }: { kicker: string; title: string; body?: string; light?: boolean }) {
  return (
    <Reveal className={`section-intro${light ? " section-intro-light" : ""}`}>
      <p className="section-kicker">{kicker}</p>
      <h2>{title}</h2>
      {body && <p>{body}</p>}
    </Reveal>
  );
}

function ChoiceGrid({ choices, value, onChange }: { choices: GuideChoice[]; value?: string; onChange: (id: string) => void }) {
  return (
    <div className="choice-grid">
      {choices.map(({ id, label, description, icon: Icon }) => (
        <motion.button
          type="button"
          key={id}
          className={`choice-card${value === id ? " is-selected" : ""}`}
          onClick={() => onChange(id)}
          whileHover={{ y: -4 }}
          whileTap={{ scale: 0.98 }}
          aria-pressed={value === id}
        >
          <span className="choice-icon"><Icon /></span>
          <span><strong>{label}</strong><small>{description}</small></span>
          <span className="choice-check" aria-hidden="true"><Check /></span>
        </motion.button>
      ))}
    </div>
  );
}

function NextLifeGuide() {
  const [step, setStep] = useState(0);
  const [category, setCategory] = useState<string>();
  const [condition, setCondition] = useState<string>();
  const [priority, setPriority] = useState<string>();

  const path = useMemo<PathKey>(() => {
    if (condition === "end") return "recycle";
    if (condition === "repair" || condition === "care") return "repair";
    if (priority === "keep") return "lend";
    if (priority === "impact") return "donate";
    if (priority === "fast") return "give";
    return "trade";
  }, [condition, priority]);

  const content = pathContent[path];
  const steps = [
    { label: "What is it?", choices: categories, value: category, setValue: setCategory },
    { label: "What shape is it in?", choices: conditions, value: condition, setValue: setCondition },
    { label: "What matters most?", choices: priorities, value: priority, setValue: setPriority },
  ];
  const current = steps[step];
  const canContinue = current?.value;

  const reset = () => {
    setStep(0);
    setCategory(undefined);
    setCondition(undefined);
    setPriority(undefined);
  };

  return (
    <section className="guide-section section" id="guide" aria-labelledby="guide-title">
      <div className="shell guide-shell">
        <SectionIntro
          kicker="The Next Life Guide"
          title="Start with one useful thing."
          body="A short, human conversation points toward a useful local route. This preview shows the complete experience GreenSwap is building toward."
        />
        <div className="guide-frame">
          <div className="guide-rail" aria-label="Guide progress">
            {["The thing", "Its condition", "Your priority", "Next life"].map((label, index) => (
              <div className={`guide-marker${index === step ? " is-current" : ""}${index < step ? " is-complete" : ""}`} key={label}>
                <span>{index < step ? <Check /> : index + 1}</span><p>{label}</p>
              </div>
            ))}
          </div>
          <div className="guide-panel" aria-live="polite">
            <AnimatePresence mode="wait">
              {step < 3 && current ? (
                <motion.div key={step} initial={{ opacity: 0, x: 18 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -18 }} transition={{ duration: 0.28 }}>
                  <p className="guide-step">Question {step + 1} of 3</p>
                  <h3>{current.label}</h3>
                  <ChoiceGrid choices={current.choices} value={current.value} onChange={current.setValue} />
                  <div className="guide-controls">
                    <button className="button button-quiet" type="button" onClick={() => setStep((value) => Math.max(0, value - 1))} disabled={step === 0}><ChevronLeft /> Back</button>
                    <button className="button button-dark" type="button" onClick={() => setStep((value) => value + 1)} disabled={!canContinue}>{step === 2 ? "Show its next life" : "Continue"} <ArrowRight /></button>
                  </div>
                </motion.div>
              ) : (
                <motion.div className="guide-result" key="result" initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}>
                  <div className="result-icon"><content.icon /></div>
                  <p className="guide-step">{content.eyebrow}</p>
                  <h3>{content.title}</h3>
                  <p className="result-body">{content.body}</p>
                  <div className="result-alternatives">
                    <span>Also worth considering</span>
                    {content.alternatives.map((item) => <p key={item}><Check /> {item}</p>)}
                  </div>
                  <div className="guide-controls">
                    <button className="button button-quiet" type="button" onClick={reset}><RefreshCw /> Start again</button>
                    <a className="button button-dark" href={APP_URL}>Continue in GreenSwap <ArrowRight /></a>
                  </div>
                  <p className="result-note"><ShieldCheck /> This is a route recommendation, not an environmental-impact claim. Availability depends on local participants and partners.</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}

function HomeRecognition() {
  const pains = [
    { icon: Boxes, title: "I know I have one somewhere.", body: "Useful things disappear in closets, boxes, garages, and storage." },
    { icon: Search, title: "I bought another one.", body: "I knew I had one, but I couldn't find it." },
    { icon: Route, title: "Hmm... maybe a friend could use this?", body: "I wish my friends could see what I'm giving away." },
  ];
  return (
    <section className="home-recognition section" id="home-recognition" aria-labelledby="home-recognition-title">
      <div className="shell">
        <SectionIntro kicker="A familiar problem" title="Useful things should not become clutter." body="GreenSwap begins with a simple truth: many things still have value, but people cannot always see what they have, find someone who needs it, or choose a responsible next step." />
        <h2 className="sr-only" id="home-recognition-title">Problems GreenSwap helps people solve</h2>
        <div className="home-recognition-grid">
          {pains.map(({ icon: Icon, title, body }, index) => <article key={title}><span>0{index + 1}</span><Icon /><h3>{title}</h3><p>{body}</p></article>)}
        </div>
      </div>
    </section>
  );
}

function HomeHowItWorks() {
  return (
    <section className="home-how-it-works section" id="home-how-it-works" aria-labelledby="home-how-it-works-title">
      <div className="shell">
        <SectionIntro kicker="Start with what you already have" title="See it. Find it. Keep it useful." body="GreenSwap helps you understand what you own before deciding what should stay, move, or begin a new life." />
        <h2 className="sr-only" id="home-how-it-works-title">How GreenSwap works</h2>
        <div className="public-start-step-grid">
          <article><Boxes /><span>01</span><h3>Catalogue your things</h3><p>Remember what is in closets, boxes, garages, and storage.</p></article>
          <article><Search /><span>02</span><h3>Find what you need</h3><p>Locate what you already own before buying it again.</p></article>
          <article><RefreshCw /><span>03</span><h3>Circulate when ready</h3><p>Lend, trade, or give useful items within communities you trust.</p></article>
        </div>
        <div className="home-how-actions"><a className="button button-dark" href={BROWSE_URL}>Explore GreenSwap items <ArrowRight /></a><a className="text-button" href="/communities">Explore communities</a></div>
      </div>
    </section>
  );
}

function HomeConversationEntry() {
  return (
    <section className="home-conversation-entry section" aria-labelledby="home-conversation-title">
      <div className="shell home-conversation-card">
        <HeartHandshake />
        <div><p className="section-kicker">Bring the opportunity you already see</p><h2 id="home-conversation-title">You do not need to have every answer.</h2><p>Tell GreenSwap about the useful things, people, or community you want to connect. We will help find a responsible starting point with the people best placed to help.</p></div>
        <div><a className="button button-primary" href="/connect?source=%2F">Talk with GreenSwap <ArrowRight /></a><a className="button button-glass" href="/partners">Explore ways to get involved</a></div>
      </div>
    </section>
  );
}

type JourneyGoalId = "personal" | "housing" | "services" | "organization" | "community_access";

const journeyUrlValues: Record<JourneyGoalId, string> = {
  personal: "personal",
  housing: "housing",
  services: "reuse",
  organization: "workplace",
  community_access: "community",
};

function journeyFromUrl(): JourneyGoalId | undefined {
  const value = new URLSearchParams(window.location.search).get("journey");
  return (Object.entries(journeyUrlValues).find(([, urlValue]) => urlValue === value)?.[0] as JourneyGoalId | undefined);
}

function writeJourneyToUrl(journey: JourneyGoalId): void {
  const url = new URL(window.location.href);
  url.searchParams.set("journey", journeyUrlValues[journey]);
  window.history.pushState({}, "", `${url.pathname}${url.search}${url.hash}`);
}

type JourneyGoalBase = {
  id: JourneyGoalId;
  prompt: string;
  shortLabel: string;
  icon: LucideIcon;
  eyebrow: string;
  title: string;
  body: string;
  primaryLabel: string;
  primaryHref: string;
  inquiryRole?: PartnerInquiryRole;
};

type JourneyStoryGoal = JourneyGoalBase & {
  visual?: "story";
  image: string;
  beforeImage?: string;
  beforeWidth: number;
  beforeHeight: number;
  afterWidth: number;
  afterHeight: number;
  alt: string;
  beforeLabel: string;
  beforeBody: string;
  afterLabel: string;
  afterBody: string;
};

type CommunityAccessGoal = JourneyGoalBase & { visual: "community_access" };
type JourneyGoal = JourneyStoryGoal | CommunityAccessGoal;

const journeyGoals: JourneyGoal[] = [
  {
    id: "personal",
    prompt: "Organize and share my things",
    shortLabel: "My things",
    icon: PackageCheck,
    eyebrow: "For people with useful things",
    title: "Give every useful thing a place and a next step.",
    body: "Catalogue what you own and where you keep it. When something is ready to move on, lend, trade, or give it to people you trust nearby.",
    image: "personal-circulation-story",
    beforeWidth: 705,
    beforeHeight: 941,
    afterWidth: 1271,
    afterHeight: 1237,
    alt: "The same useful drill, folding chair, storage tote, and games move from a crowded garage into friendly, realistic handoffs among neighbors.",
    beforeLabel: "Useful, but out of sight",
    beforeBody: "Good things disappear into closets, boxes, and storage.",
    afterLabel: "Known and useful",
    afterBody: "You know what you have, where it is, and what is ready to share.",
    primaryLabel: "Find an item’s next life",
    primaryHref: "/start#guide",
  },
  {
    id: "housing",
    prompt: "Help residents circulate useful items",
    shortLabel: "Housing",
    icon: Building2,
    eyebrow: "For apartment and community teams",
    title: "Help useful move-out items find their next home.",
    body: "Connect residents before furniture and household goods become hallway clutter, hauling work, or preventable waste.",
    image: "housing-next-life-story",
    beforeWidth: 836,
    beforeHeight: 941,
    afterWidth: 836,
    afterHeight: 941,
    alt: "A property manager first finds useful furniture left unattended, then helps residents connect so the same items begin a new life.",
    beforeLabel: "Left behind for staff",
    beforeBody: "Useful items become hallway clutter, cleanup work, and disposal risk.",
    afterLabel: "Matched before move-out",
    afterBody: "Earlier resident handoffs reduce staff burden and keep good things in use.",
    primaryLabel: "Explore the housing pilot",
    primaryHref: "/partners/housing",
    inquiryRole: "property_manager",
  },
  {
    id: "services",
    prompt: "Offer donation, lending, repair, or reuse services",
    shortLabel: "Reuse services",
    icon: HandHeart,
    eyebrow: "For nonprofits and reuse services",
    title: "Receive what your mission can truly use.",
    body: "Share current needs and acceptance rules before the handoff, so suitable donations reach your team and other items can find a better route.",
    image: "reuse-service-story",
    beforeImage: "reuse-service-story-before-v3",
    beforeWidth: 1182,
    beforeHeight: 1331,
    afterWidth: 836,
    afterHeight: 941,
    alt: "At a community reuse nonprofit, a coordinator respectfully pauses an unusable cracked television donation. In a separate scene, the same donor brings a working red sewing machine matched to a real program need.",
    beforeLabel: "Good intent, unusable item",
    beforeBody: "A damaged device creates inspection, storage, and responsible disposal work.",
    afterLabel: "Matched to a real need",
    afterBody: "A working sewing machine arrives ready for a program that needs it.",
    primaryLabel: "Explore the reuse pilot",
    primaryHref: "/partners/reuse-organizations",
    inquiryRole: "nonprofit_partner",
  },
  {
    id: "organization",
    prompt: "Put workplace or community resources to use",
    shortLabel: "My organization",
    icon: Factory,
    eyebrow: "For employers and community builders",
    title: "Turn approved resources into useful outcomes.",
    body: "Create a trusted path for approved devices, equipment, and supplies to reach employees or verified local partners under clear rules, with confirmed outcomes reported responsibly.",
    image: "workplace-community-story",
    beforeWidth: 677,
    beforeHeight: 941,
    afterWidth: 995,
    afterHeight: 941,
    alt: "The same lamps, chair, monitor, and tool case move from forgotten workplace storage into an organized employee circulation event.",
    beforeLabel: "Useful assets, sitting idle",
    beforeBody: "Equipment and supplies stay boxed up when ownership, eligibility, and release rules are unclear.",
    afterLabel: "Approved and in circulation",
    afterBody: "A trusted program helps people find and share eligible items while the organization maintains oversight.",
    primaryLabel: "Explore the workplace pilot",
    primaryHref: "/partners/workplaces",
    inquiryRole: "employer_partner",
  },
  {
    id: "community_access",
    prompt: "Help more people join my group",
    shortLabel: "Welcome newcomers",
    icon: Users,
    eyebrow: "For clubs, groups, and prospective members",
    title: "Make the first step easier.",
    body: "Help newcomers borrow, trade, or receive suitable starter equipment, so buying everything upfront does not determine who gets to participate.",
    visual: "community_access",
    primaryLabel: "Explore community groups",
    primaryHref: "/partners/community-groups",
    inquiryRole: "community_group",
  },
];

function StoryVisual({ goal, className = "" }: { goal: JourneyStoryGoal; className?: string }) {
  const beforeRatio = goal.beforeWidth / goal.beforeHeight;
  const afterRatio = goal.afterWidth / goal.afterHeight;
  const beforeImage = goal.beforeImage ?? `${goal.image}-before`;
  const sceneColumns = `${beforeRatio}fr ${afterRatio}fr`;
  const storyAspectRatio = `${beforeRatio + afterRatio} / 1`;
  return (
    <figure className={`housing-story-visual role-story-visual ${className}`.trim()}>
      <div className="story-image-grid" style={{ gridTemplateColumns: sceneColumns, aspectRatio: storyAspectRatio }}>
        <picture>
          <source srcSet={`/assets/${beforeImage}.webp`} type="image/webp" />
          <img src={`/assets/${beforeImage}.png`} alt={`${goal.beforeLabel}: ${goal.beforeBody}`} width={goal.beforeWidth} height={goal.beforeHeight} loading="lazy" draggable={false} />
        </picture>
        <picture>
          <source srcSet={`/assets/${goal.image}-after.webp`} type="image/webp" />
          <img src={`/assets/${goal.image}-after.png`} alt={`${goal.afterLabel}: ${goal.afterBody}`} width={goal.afterWidth} height={goal.afterHeight} loading="lazy" draggable={false} />
        </picture>
      </div>
      <figcaption style={{ gridTemplateColumns: sceneColumns }}>
        <span><b>{goal.beforeLabel}</b>{goal.beforeBody}</span>
        <span><b>{goal.afterLabel}</b>{goal.afterBody}</span>
      </figcaption>
    </figure>
  );
}

function HomeTransformation() {
  const personalGoal = journeyGoals.find((goal): goal is JourneyStoryGoal => goal.id === "personal" && goal.visual !== "community_access");
  if (!personalGoal) return null;
  return (
    <section className="home-transformation section" aria-labelledby="home-transformation-title">
      <div className="shell home-transformation-layout">
        <div>
          <p className="section-kicker">From hidden to useful</p>
          <h2 id="home-transformation-title">Free up space without giving up on useful things.</h2>
          <p>GreenSwap helps turn forgotten belongings into things you can find, keep, lend, trade, or give when the right moment arrives.</p>
          <a className="button button-dark" href="/start">See the complete GreenSwap flow <ArrowRight /></a>
        </div>
        <StoryVisual goal={personalGoal} className="home-transformation-story" />
      </div>
    </section>
  );
}

const communityAccessScenes = [
  {
    image: "community-access-hiking",
    label: "Hiking & camping",
    title: "Find your fit before your first trail.",
    body: "Borrow a pack, poles, or starter gear while learning what works for you.",
    alt: "An experienced hiking-club member helps a newcomer adjust a teal borrowed backpack before a group hike.",
  },
  {
    image: "community-access-arts",
    label: "Arts & crafts",
    title: "Try the craft before building your own kit.",
    body: "Learn with shared equipment while discovering the tools and materials you enjoy.",
    alt: "A textile-club member guides a newcomer using a shared sewing machine in a community workshop.",
  },
  {
    image: "community-access-golf",
    label: "Golf & recreation",
    title: "Learn your swing before choosing your set.",
    body: "Try suitable beginner equipment before investing in gear that may not fit.",
    alt: "An experienced community golfer helps a newcomer try a correctly sized shared club at a public driving range.",
  },
  {
    image: "community-access-makers",
    label: "Robotics & makers",
    title: "Build your first project with shared tools.",
    body: "Start learning with supervised equipment, components, and experienced members.",
    alt: "A maker-club mentor helps a newcomer attach a wheel to a small robot using shared tools at a workbench.",
  },
];

function CommunityAccessGrid() {
  return (
    <div className="community-access-visual" aria-label="Ways community equipment can help newcomers participate">
      <div className="community-access-grid">
        {communityAccessScenes.map(({ image, label, title, body, alt }) => (
          <article key={image}>
            <picture>
              <source srcSet={`/assets/${image}.webp`} type="image/webp" />
              <img src={`/assets/${image}.png`} width="1448" height="1086" draggable={false} alt={alt} />
            </picture>
            <div><span>{label}</span><strong>{title}</strong><p>{body}</p></div>
          </article>
        ))}
      </div>
      <a className="community-access-member-link" href="/communities">Looking for a group? Explore communities <ArrowRight /></a>
    </div>
  );
}

function JourneyDiscovery({ onOpenInquiry }: { onOpenInquiry: (role?: string) => void }) {
  const initialJourney = useMemo(journeyFromUrl, []);
  const [selected, setSelected] = useState<JourneyGoalId | undefined>(initialJourney);
  const [choosing, setChoosing] = useState(!initialJourney);
  const [champion, setChampion] = useState(false);
  const selectionSummaryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const restoreFromHistory = () => {
      const restored = journeyFromUrl();
      setSelected(restored);
      setChoosing(!restored);
    };
    window.addEventListener("popstate", restoreFromHistory);
    return () => window.removeEventListener("popstate", restoreFromHistory);
  }, []);

  const chooseJourney = (journey: JourneyGoalId) => {
    setSelected(journey);
    setChoosing(false);
    writeJourneyToUrl(journey);
    window.requestAnimationFrame(() => selectionSummaryRef.current?.focus({ preventScroll: true }));
  };

  const activeGoal = journeyGoals.find((goal) => goal.id === selected);
  const resolvedGoal = activeGoal ?? journeyGoals[0];
  const championCopy = resolvedGoal.id === "personal"
    ? {
        title: "Start with your things, then bring others along.",
        body: "Try GreenSwap yourself, or introduce the idea to your building, workplace, school, or group. We will help you frame the opportunity for the person best placed to help.",
        action: "Help me introduce GreenSwap",
      }
    : resolvedGoal.id === "housing"
      ? {
          title: "You can start the conversation from any role.",
          body: "Share the recurring move-out problem and any property contact you know. GreenSwap will help you prepare a concise introduction for the person who can explore a pilot.",
          action: "Prepare a housing introduction",
        }
    : resolvedGoal.id === "services"
      ? {
          title: "You can start with the need your team already sees.",
          body: "Share the intake, storage, or service challenge and any contact you know within the organization. GreenSwap will help you prepare a concise introduction for the person who can explore a pilot.",
          action: "Prepare a reuse introduction",
        }
    : resolvedGoal.id === "community_access"
      ? {
          title: "You can help your group welcome more people.",
          body: "Share which starter equipment creates the most friction, what experienced members could offer, and who helps organize the group. GreenSwap will help you open a practical conversation about access.",
          action: "Prepare a group introduction",
        }
    : {
        title: "You can bring the right teams a credible first step.",
        body: "Name what is sitting idle, who could benefit, and who controls release. GreenSwap will help you introduce a bounded pilot to IT, facilities, sustainability, community impact, or another responsible owner.",
        action: "Prepare a workplace introduction",
      };
  const championInquiryRole: PartnerInquiryRole = resolvedGoal.id === "housing"
    ? "resident_champion"
    : resolvedGoal.id === "services"
      ? "nonprofit_partner"
      : resolvedGoal.id === "organization"
        ? "employer_partner"
        : resolvedGoal.id === "community_access"
          ? "community_group"
          : "resident_champion";

  return (
    <section className="journey-discovery section" id="journey-discovery" aria-labelledby="journey-discovery-title">
      <div className="shell">
        <SectionIntro kicker="An optional next step" title="What would you like to make possible?" body="You already know what GreenSwap offers. Choose a goal only if you want a more relevant starting point." />
        {choosing && <>
          <fieldset className="journey-choice-fieldset">
            <legend className="sr-only">Choose your GreenSwap journey</legend>
            <div className="journey-choice-grid">
              {journeyGoals.map(({ id, prompt, icon: Icon }) => {
                const isSelected = selected === id;
                return <label key={id} className={isSelected ? "is-selected" : ""}><input type="radio" name="greenswap-journey" value={id} checked={isSelected} onChange={() => chooseJourney(id)} /><Icon /><span>{prompt}</span><span className="journey-choice-check" aria-hidden="true" /></label>;
              })}
            </div>
          </fieldset>
          <div className="journey-choice-footer"><a href="/start">Explore everything GreenSwap offers <ArrowRight /></a>{selected && <button type="button" onClick={() => setChoosing(false)}>Keep my current journey</button>}</div>
        </>}

        {activeGoal && !choosing && <>
          <p className="sr-only" aria-live="polite">Selected journey: {activeGoal.prompt}</p>
          <div className="journey-selection-summary" ref={selectionSummaryRef} tabIndex={-1}>
            <div><span>Your journey</span><strong>{activeGoal.prompt}</strong></div>
            <button type="button" onClick={() => setChoosing(true)}>Change</button>
            <a href="/start">Explore all</a>
          </div>
          <div className="journey-result">
            <motion.article key={activeGoal.id} className="journey-story" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
              <div className="journey-story-copy"><p>{activeGoal.eyebrow}</p><h3>{activeGoal.title}</h3><span>{activeGoal.body}</span><div className="journey-story-actions"><a className="button button-dark" href={activeGoal.primaryHref}>{activeGoal.primaryLabel} <ArrowRight /></a>{activeGoal.inquiryRole && <button className="button button-quiet" type="button" onClick={() => onOpenInquiry(activeGoal.inquiryRole)}>Talk with GreenSwap</button>}</div></div>
              {activeGoal.visual === "community_access" ? <CommunityAccessGrid /> : <StoryVisual goal={activeGoal} />}
            </motion.article>
            <button type="button" className={`champion-choice${champion ? " is-selected" : ""}`} aria-pressed={champion} onClick={() => setChampion((value) => !value)}><HeartHandshake /><span><strong>Optional: I am championing this idea</strong><small>Add help for starting the right conversation in my community or company.</small></span><span className="journey-choice-check" aria-hidden="true">{champion && <Check />}</span></button>
            {champion && <aside className="champion-next-step"><HeartHandshake /><div><strong>{championCopy.title}</strong><p>{championCopy.body}</p></div><button className="button button-primary" type="button" onClick={() => onOpenInquiry(championInquiryRole)}>{championCopy.action} <ArrowRight /></button></aside>}
          </div>
        </>}
      </div>
    </section>
  );
}

function Journey() {
  const events = [
    { year: "2018", title: "Made useful", body: "A lamp enters its first home.", icon: Factory },
    { year: "2026", title: "Ready to move", body: "Its owner asks GreenSwap what should happen next.", icon: Route },
    { year: "Next", title: "Found nearby", body: "A neighbor gives it a new room and a reason to glow again.", icon: Home },
    { year: "Later", title: "Cared for", body: "A repair event can become part of its trustworthy history.", icon: Wrench },
  ];
  return (
    <section className="journey section" id="journey" aria-labelledby="journey-title">
      <div className="shell">
        <SectionIntro kicker="A longer story" title="What if useful things had a memory?" body="GreenSwap begins with a handoff. The roadmap grows that moment into an item history, so care, custody, repair, and reuse can survive each change of hands." />
        <div className="journey-layout">
          <div className="passport-card">
            <div className="passport-top"><div className="lamp-orbit"><span className="lamp-shade" /><span className="lamp-stem" /></div><p>Resource passport</p><span>GS · LAMP · 0042</span></div>
            <div className="passport-meta"><div><span>Status</span><strong>In use</strong></div><div><span>Verified events</span><strong>3</strong></div><div><span>Useful lives</span><strong>2+</strong></div></div>
            <p className="passport-disclaimer">Illustrative future experience. Not a live item record.</p>
          </div>
          <div className="timeline">
            {events.map(({ year, title, body, icon: Icon }, index) => (
              <Reveal className="timeline-event" key={title} delay={index * 0.08}>
                <div className="timeline-icon"><Icon /></div><div><span>{year}</span><h3>{title}</h3><p>{body}</p></div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Ecosystem({ onOpenInquiry }: { onOpenInquiry: (role?: string) => void }) {
  const [active, setActive] = useState(partnerProfiles[0].id);
  const selected = partnerProfiles.find((profile) => profile.id === active)!;
  const Icon = selected.icon;
  const movePartnerFocus = (event: ReactKeyboardEvent<HTMLButtonElement>, index: number) => {
    let next = index;
    if (event.key === "ArrowRight") next = (index + 1) % partnerProfiles.length;
    else if (event.key === "ArrowLeft") next = (index - 1 + partnerProfiles.length) % partnerProfiles.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = partnerProfiles.length - 1;
    else return;
    event.preventDefault();
    const id = partnerProfiles[next].id;
    setActive(id);
    window.requestAnimationFrame(() => document.getElementById(`partner-tab-${id}`)?.focus());
  };

  return (
    <section className="ecosystem section" id="partners" aria-labelledby="partners-title">
      <div className="shell">
        <SectionIntro kicker="Partnerships over competition" title="The community is the infrastructure." body="GreenSwap should make trusted local organizations easier to discover, support, and participate in, rather than replace the work they already do well." />
        <div className="partner-lab">
          <div className="partner-tabs" role="tablist" aria-label="Organization types">
            {partnerProfiles.map(({ id, label, icon: TabIcon }, index) => (
              <button id={`partner-tab-${id}`} key={id} role="tab" aria-selected={active === id} aria-controls="partner-panel" tabIndex={active === id ? 0 : -1} onKeyDown={(event) => movePartnerFocus(event, index)} onClick={() => setActive(id)}><TabIcon /> {label}</button>
            ))}
          </div>
          <AnimatePresence mode="wait">
            <motion.div id="partner-panel" role="tabpanel" aria-labelledby={`partner-tab-${active}`} className="partner-panel" key={active} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}>
              <div className="partner-symbol"><Icon /></div>
              <div><p className="panel-kicker">For {selected.label.toLowerCase()}</p><h3>{selected.title}</h3><p>{selected.body}</p></div>
              <ul>{selected.outcomes.map((outcome) => <li key={outcome}><Check /> {outcome}</li>)}</ul>
              <button className="button button-dark" onClick={() => onOpenInquiry(selected.inquiryRole)}>Talk with GreenSwap <ArrowRight /></button>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="partner-guide-link">
          <div><p className="panel-kicker">Partner Field Guide</p><h3>Start with the problem your organization already needs to solve.</h3></div>
          <a className="button button-dark" href="/partners">Explore all partner briefs <ArrowRight /></a>
        </div>
      </div>
    </section>
  );
}

function Sponsorship({ onOpenInquiry }: { onOpenInquiry: (role?: string) => void }) {
  const [active, setActive] = useState(sponsorPrograms[0].id);
  const program = sponsorPrograms.find((item) => item.id === active)!;
  const Icon = program.icon;
  const moveProgramFocus = (event: ReactKeyboardEvent<HTMLButtonElement>, index: number) => {
    let next = index;
    if (event.key === "ArrowRight") next = (index + 1) % sponsorPrograms.length;
    else if (event.key === "ArrowLeft") next = (index - 1 + sponsorPrograms.length) % sponsorPrograms.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = sponsorPrograms.length - 1;
    else return;
    event.preventDefault();
    const id = sponsorPrograms[next].id;
    setActive(id);
    window.requestAnimationFrame(() => document.getElementById(`program-tab-${id}`)?.focus());
  };
  return (
    <section className="sponsorship section" id="sponsors" aria-labelledby="sponsor-title">
      <div className="sponsor-glow" aria-hidden="true" />
      <div className="shell sponsor-layout">
        <div>
          <SectionIntro light kicker="Community Impact Partners" title="Sponsor a local reuse economy, not an ad." body="Choose a concrete community program. Support the organizations delivering it. Receive reporting that names the method, evidence, and limitations." />
          <div className="detail-hero-actions"><a className="button button-primary" href="/sponsors">See sponsor opportunities <ArrowRight /></a><button className="button button-glass" onClick={() => onOpenInquiry("Corporate sponsor")}>Talk with GreenSwap</button></div>
        </div>
        <div className="program-studio">
          <p className="studio-label">Choose a program to preview</p>
          <div className="program-switcher" role="tablist" aria-label="Sponsor program previews">
            {sponsorPrograms.map((item, index) => <button id={`program-tab-${item.id}`} key={item.id} role="tab" aria-selected={active === item.id} aria-controls="program-panel" tabIndex={active === item.id ? 0 : -1} onKeyDown={(event) => moveProgramFocus(event, index)} onClick={() => setActive(item.id)}>{item.label}</button>)}
          </div>
          <AnimatePresence mode="wait">
            <motion.div id="program-panel" role="tabpanel" aria-labelledby={`program-tab-${active}`} className="program-card" key={active} initial={{ opacity: 0, scale: 0.985 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}>
              <div className="program-icon"><Icon /></div><p className="panel-kicker">Program concept</p><h3>{program.title}</h3><p>{program.description}</p>
              <div className="proof-list"><span>What responsible reporting could include</span>{program.proof.map((item) => <p key={item}><ShieldCheck /> {item}</p>)}</div>
              <small>Preview only. No sponsor is credited with outcomes without a documented allocation method.</small>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

const evidenceClasses = [
  { label: "Confirmed activity", body: "Recorded directly by GreenSwap or a named delivery partner", status: "Direct record", icon: BadgeCheck },
  { label: "Participant reported", body: "Confirmed by the people or organizations involved", status: "Named source", icon: Users },
  { label: "Modeled estimate", body: "Calculated with a published method, assumptions, and range", status: "Estimated", icon: Globe2 },
  { label: "Derived result", body: "Calculated from confirmed inputs without claiming direct measurement", status: "Calculated", icon: RefreshCw },
];

function EvidenceStandard() {
  return (
    <div className="impact-card">
      <div className="impact-card-head"><span>Evidence class</span><strong>How to read a claim</strong></div>
      {evidenceClasses.map(({ label, body, status, icon: Icon }) => <div className="impact-row" key={label}><Icon /><div><strong>{label}</strong><p>{body}</p></div><span>{status}</span></div>)}
      <div className="impact-note"><ShieldCheck /><p><strong>No vague green claims.</strong><br />No offsets, carbon-negative language, or sponsor attribution without evidence.</p></div>
    </div>
  );
}

function ImpactPreview() {
  return (
    <section className="home-impact-preview section" aria-labelledby="home-impact-preview-title">
      <div className="shell home-impact-preview-layout">
        <div><p className="section-kicker">Impact begins with continued use</p><h2 id="home-impact-preview-title">More room for people. More life for useful things.</h2><p>GreenSwap helps people understand what they have, connect it with a real need, and confirm what happened next. Environmental estimates remain separate from completed activity.</p><a className="button button-dark" href="/impact">Explore GreenSwap impact <ArrowRight /></a></div>
        <div className="home-impact-outcomes">
          <article><Home /><strong>Personal value</strong><span>Find what you own, free up space, and decide what is ready to move.</span></article>
          <article><Users /><strong>Community value</strong><span>Help useful things reach neighbors, members, residents, and local services.</span></article>
          <article><RefreshCw /><strong>Useful life extended</strong><span>Confirm continued use before estimating environmental outcomes.</span></article>
        </div>
      </div>
    </section>
  );
}

function ImpactPage() {
  const outcomeTypes = [
    { icon: Home, title: "Personal value", body: "Know what you have, find it when needed, free up space, and choose what is ready to circulate." },
    { icon: Users, title: "Community value", body: "Connect useful goods, shared equipment, repair help, and local needs through communities people trust." },
    { icon: RefreshCw, title: "Continued useful life", body: "Confirm that an item reached another useful destination before estimating what that change may mean." },
  ];
  const stages = [
    { label: "Available now", title: "Catalogue and circulate", body: "Members can catalogue items, remember where they are, and lend, trade, or give them through GreenSwap." },
    { label: "Pilot work", title: "Community programs", body: "GreenSwap is designing bounded programs with housing teams, nonprofits, clubs, schools, workplaces, and reuse services." },
    { label: "Developing carefully", title: "Public impact reporting", body: "Reports will separate confirmed activity, participant reports, derived results, and modeled estimates with visible limitations." },
  ];
  return (
    <main id="main" className="impact-page">
      <PageMeta path="/impact" title="GreenSwap Impact | Useful things kept useful" description="See how GreenSwap creates personal and community value, extends the useful life of existing goods, and reports evidence without overstating environmental outcomes." />
      <section className="impact-page-hero">
        <div className="shell impact-page-hero-layout"><div><p className="eyebrow eyebrow-light"><span>GreenSwap impact</span></p><h1>Useful things kept useful. <em>Communities made stronger.</em></h1><p>Impact begins when people can see what they have, connect it with a real need, and confirm what happened next.</p><div className="detail-hero-actions"><a className="button button-primary" href={BROWSE_URL}>Explore GreenSwap items <ArrowRight /></a><a className="button button-glass" href="#how-impact-happens">See how impact happens <ArrowDown /></a></div></div><div className="impact-page-principle"><PackageCheck /><span>Our starting point</span><strong>Keep useful things visible, available, and moving toward real needs.</strong><p>Organization is a personal benefit. A confirmed useful outcome is evidence. Environmental impact remains an estimate unless directly measured.</p></div></div>
      </section>

      <section className="impact-outcome-section section" aria-labelledby="impact-outcome-title"><div className="shell"><SectionIntro kicker="Value people can feel" title="Impact has more than one dimension." body="GreenSwap separates immediate human and community benefits from environmental estimates so every claim stays within its evidence." /><h2 className="sr-only" id="impact-outcome-title">Types of GreenSwap impact</h2><div className="impact-outcome-grid">{outcomeTypes.map(({icon: Icon,title,body})=><article key={title}><Icon /><h3>{title}</h3><p>{body}</p></article>)}</div></div></section>

      <section className="impact-sequence section" id="how-impact-happens" aria-labelledby="impact-sequence-title"><div className="shell"><SectionIntro kicker="One item, one accountable path" title="Impact begins before the handoff." body="A listing or intention is not an outcome. GreenSwap follows the item toward a useful, confirmed next step." /><h2 className="sr-only" id="impact-sequence-title">How GreenSwap impact happens</h2><ol><li><span>01</span><Search /><div><strong>See what already exists</strong><p>Catalogue the item, its location, condition, and current owner.</p></div></li><li><span>02</span><HeartHandshake /><div><strong>Connect it with a real need</strong><p>Lend, trade, give, repair, or route it through an appropriate community or service.</p></div></li><li><span>03</span><BadgeCheck /><div><strong>Confirm what happened</strong><p>Record the completed activity before calculating or communicating broader impact.</p></div></li></ol></div></section>

      <section className="impact-stage-section section" aria-labelledby="impact-stage-title"><div className="shell"><SectionIntro kicker="The current stage" title="Show what is real now, and what is still being built." body="GreenSwap is early. That makes honest boundaries more useful than impressive-looking totals." /><h2 className="sr-only" id="impact-stage-title">Current GreenSwap impact capabilities</h2><div className="impact-stage-grid">{stages.map(({label,title,body})=><article key={label}><span>{label}</span><h3>{title}</h3><p>{body}</p></article>)}</div></div></section>

      <section className="impact-evidence-section section" aria-labelledby="impact-evidence-title"><div className="shell impact-layout"><SectionIntro kicker="Evidence before claims" title="Make every claim earn its confidence." body="Public reporting should identify the evidence class, reporting period, methodology, assumptions, and limitations behind every result." /><div><h2 className="sr-only" id="impact-evidence-title">GreenSwap evidence standard</h2><EvidenceStandard /></div></div></section>

      <section className="impact-report-section section"><div className="shell impact-report-card"><div><p className="section-kicker">Reports and methodology</p><h2>No placeholder success stories.</h2><p>GreenSwap will publish pilot recaps when evidence is ready. Until then, we explain the method, current limitations, and what still needs to be measured.</p></div><div><a className="button button-dark" href="/about">Read our operating principles <ArrowRight /></a><a className="text-button" href="/sponsors">How sponsors support accountable pilots</a></div></div></section>

      <section className="impact-actions section"><div className="shell"><SectionIntro light kicker="Choose your next step" title="Help useful things create value where you live or work." /><div className="impact-action-grid"><a href={BROWSE_URL}><PackageCheck /><strong>Explore useful items</strong><span>See what GreenSwap members have made public.</span><ArrowRight /></a><a href="/connect?source=%2Fimpact"><Users /><strong>Start a community conversation</strong><span>Bring a local need, organization, or pilot idea.</span><ArrowRight /></a><a href="/sponsors"><CircleDollarSign /><strong>Fund local capacity</strong><span>Support a bounded pilot with honest reporting.</span><ArrowRight /></a></div></div></section>
    </main>
  );
}

function Roadmap() {
  return (
    <section className="roadmap section" id="roadmap" aria-labelledby="roadmap-title">
      <div className="shell">
        <SectionIntro kicker="Built in trustworthy layers" title="From one handoff to shared local infrastructure." body="The public story distinguishes what people can use today from the partner network and open infrastructure GreenSwap is building toward." />
        <div className="roadmap-track">
          {roadmap.map((item, index) => (
            <Reveal className="roadmap-card" key={item.phase} delay={index * 0.08}>
              <div className="roadmap-number">0{index + 1}</div><p className="roadmap-phase">{item.phase}</p><h3>{item.title}</h3><p>{item.body}</p><span>{item.status}</span>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQ() {
  const items = [
    ["Is GreenSwap another resale marketplace?", "No. Trading is a strong first loop, but the mission is broader: guide useful things toward trade, giving, lending, qualified donation, repair, and responsible end-of-life routes."],
    ["Are all partner features available now?", "Not to everyone. The app supports neighbor-to-neighbor circulation today, while organization, lending, repair, donation, and sponsorship tools are being introduced through approved pilots behind feature, evidence, and trust gates."],
    ["How will GreenSwap report environmental impact?", "With labeled evidence classes, named methodologies, visible assumptions, and a clear separation between measured, reported, modeled, and derived outcomes."],
    ["Will GreenSwap replace local organizations?", "No. The partnership principle is explicit: connect people to organizations already doing the work well, and reduce friction around discovery, referrals, and evidence."],
    ["Can my organization join a pilot?", "Yes. If your organization or community sees a useful way to help items stay in use, make participation easier, or strengthen local circulation, we would like to hear it. Use the form below to tell us what you would like to make possible."],
  ];
  return (
    <section className="faq section" aria-labelledby="faq-title">
      <div className="shell faq-layout">
        <SectionIntro kicker="Questions worth asking" title="Clear before clever." />
        <div className="faq-list">
          {items.map(([question, answer]) => <details key={question}><summary>{question}<ChevronDown /></summary><p>{answer}</p></details>)}
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div className="shell footer-top">
        <div><Brand /><p>Helping every useful thing find its next life.</p></div>
        <div className="footer-links">
          <div><strong>Explore</strong><a href="/start">How GreenSwap works</a><a href="/partners">Get involved</a><a href="/sponsors">Fund a pilot</a><a href="/impact">Impact</a></div>
          <div><strong>GreenSwap</strong><a href={SIGN_IN_URL}>Sign in</a><a href={REGISTER_URL}>Join GreenSwap</a><a href={APP_URL}>Open the app</a><a href="/connect">Start a conversation</a></div>
          <div><strong>Trust</strong><a href="/about">About</a><a href="/privacy">Privacy</a><a href="/terms">Terms</a><a href="/accessibility">Accessibility</a><button className="footer-link-button" type="button" onClick={() => window.dispatchEvent(new Event("greenswap:open-privacy-choices"))}>Privacy choices</button></div>
        </div>
      </div>
      <div className="shell footer-bottom"><p>© {new Date().getFullYear()} GreenSwap. Keep useful items in use.</p><p>Mission before metrics. Evidence before claims. · <a href="mailto:info@greenswap.us">Contact GreenSwap</a></p></div>
    </footer>
  );
}

type PaperSize = "letter" | "a4";
type PosterFinish = "color" | "ink";
type PartnerGuideCategory = "communities" | "institutions" | "reuse" | "sponsors";

const partnerGuideInventory: Array<{
  title: string;
  audience: string;
  promise: string;
  badge?: string;
  thought?: string;
  status: "ready" | "planned";
  href?: string;
  icon: LucideIcon;
  category: PartnerGuideCategory;
}> = [
  { title: "Housing", audience: "Apartment, HOA & residential teams", promise: "Turn move-out waste into move-in value.", badge: "For housing teams", thought: "I wish a new resident could use this instead of throwing it away.", status: "ready", href: "/partners/housing", icon: Building2, category: "communities" },
  { title: "Community groups", audience: "Clubs, circles & hobby communities", promise: "Help more people join with what your group already has.", badge: "For clubs & groups", thought: "A newcomer could borrow this before deciding what to buy.", status: "ready", href: "/partners/community-groups", icon: Users, category: "communities" },
  { title: "Workplace surplus", audience: "Employers & facilities teams", promise: "Retire assets, not their usefulness.", badge: "For workplace teams", thought: "Someone here could use this instead of us disposing of it.", status: "ready", href: "/partners/workplaces", icon: Factory, category: "institutions" },
  { title: "Community sponsorship", audience: "Corporate citizenship & sustainability teams", promise: "Fund local reuse you can verify.", badge: "For impact sponsors", thought: "I want our support to create something local we can actually verify.", status: "ready", href: "/sponsors", icon: CircleDollarSign, category: "sponsors" },
  { title: "Schools & universities", audience: "Campus operations & educators", promise: "Keep campus resources in circulation.", badge: "For schools & campuses", thought: "Another class could use this instead of buying it again.", status: "ready", href: "/partners/schools-colleges", icon: GraduationCap, category: "institutions" },
  { title: "Cities & waste agencies", audience: "Public works & sustainability teams", promise: "Prevent useful goods from becoming public cleanup.", badge: "For public agencies", thought: "There should be a better option before this becomes public cleanup.", status: "ready", href: "/partners/public-agencies", icon: Globe2, category: "institutions" },
  { title: "Reuse organizations", audience: "Furniture banks & community nonprofits", promise: "Receive what you need, not what you must dispose of.", badge: "For reuse organizations", thought: "We could help more people if the right items reached us.", status: "ready", href: "/partners/reuse-organizations", icon: HandHeart, category: "reuse" },
  { title: "Tool libraries", audience: "Shared-equipment stewards", promise: "Grow access without replacing the system that already works.", badge: "For tool libraries", thought: "Could GreenSwap help more neighbors find and support what we already offer?", status: "ready", href: "/partners/tool-libraries", icon: Library, category: "reuse" },
  { title: "Repair organizations", audience: "Repair cafés, shops & volunteers", promise: "Turn repair interest into better-qualified requests.", badge: "For repair teams", thought: "Could we understand the item and need before it reaches our volunteers?", status: "ready", href: "/partners/repair-organizations", icon: Wrench, category: "reuse" },
];

function PageMeta({ title, description, path, robots = "index,follow" }: { title: string; description: string; path?: string; robots?: string }) {
  useEffect(() => {
    document.title = title;
    const canonicalUrl = `https://greenswap.us${path ?? window.location.pathname}`;
    const setMeta = (selector: string, attribute: "name" | "property", key: string, content: string) => {
      let element = document.querySelector<HTMLMetaElement>(selector);
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      element.content = content;
    };
    setMeta('meta[name="description"]', "name", "description", description);
    setMeta('meta[name="robots"]', "name", "robots", robots);
    setMeta('meta[property="og:title"]', "property", "og:title", title);
    setMeta('meta[property="og:description"]', "property", "og:description", description);
    setMeta('meta[property="og:url"]', "property", "og:url", canonicalUrl);
    setMeta('meta[name="twitter:title"]', "name", "twitter:title", title);
    setMeta('meta[name="twitter:description"]', "name", "twitter:description", description);
    let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl;
    const navigationEntry = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
    const isHistoryTraversal = navigationEntry?.type === "back_forward";
    if (isHistoryTraversal) return;

    const frame = window.requestAnimationFrame(() => {
      const targetId = window.location.hash.slice(1);
      const target = targetId ? document.getElementById(decodeURIComponent(targetId)) : null;
      if (target) {
        const previousScrollBehavior = document.documentElement.style.scrollBehavior;
        document.documentElement.style.scrollBehavior = "auto";
        target.scrollIntoView({ block: "start" });
        document.documentElement.style.scrollBehavior = previousScrollBehavior;
      }
      else window.scrollTo({ top: 0, left: 0 });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [title, description, path, robots]);
  return null;
}

function PrintBrand() {
  return (
    <div className="brief-brand" aria-label="GreenSwap">
      <img src="/branding/greenswap-mark.svg" alt="" aria-hidden="true" draggable={false} />
      <span><strong>Green</strong><strong>Swap</strong></span>
    </div>
  );
}

function HousingStoryVisual({ variant }: { variant: "hero" | "brief" }) {
  return (
    <figure className={`housing-story-visual housing-story-visual--${variant}`}>
      <div className="story-image-grid">
        <picture>
          <source srcSet="/assets/housing-next-life-story-before.webp" type="image/webp" />
          <img
            src="/assets/housing-next-life-story-before.png"
            alt="A concerned property manager finds a chair, lamp, and box of household goods left unattended beside a handwritten Free sign."
            width="836"
            height="941"
            loading={variant === "hero" ? "eager" : "lazy"}
            fetchPriority={variant === "hero" ? "high" : "auto"}
            draggable={false}
          />
        </picture>
        <picture>
          <source srcSet="/assets/housing-next-life-story-after.webp" type="image/webp" />
          <img
            src="/assets/housing-next-life-story-after.png"
            alt="The property manager introduces residents as the same chair, lamp, and household goods move to their next owners."
            width="836"
            height="941"
            loading={variant === "hero" ? "eager" : "lazy"}
            fetchPriority={variant === "hero" ? "high" : "auto"}
            draggable={false}
          />
        </picture>
      </div>
      <figcaption>
        <span><b>Without an earlier route</b>Useful items become a property problem.</span>
        <span><b>With GreenSwap</b>Neighbors connect before value becomes waste.</span>
      </figcaption>
    </figure>
  );
}

function BriefToolbar({ guide, paper, onPaperChange }: { guide: "housing" | "community-groups" | "sponsorship"; paper: PaperSize; onPaperChange: (paper: PaperSize) => void }) {
  const pdfHref = `/downloads/greenswap-${guide}-field-guide-${paper}.pdf`;
  return (
    <div className="brief-toolbar shell" aria-label="Printable brief controls">
      <div>
        <p className="section-kicker">Shareable one-page brief</p>
        <p>Choose a paper size, then open the PDF to print, save, or share.</p>
      </div>
      <div className="brief-controls">
        <div className="paper-switch" role="group" aria-label="Paper size">
          <button type="button" aria-pressed={paper === "letter"} onClick={() => onPaperChange("letter")}>US Letter</button>
          <button type="button" aria-pressed={paper === "a4"} onClick={() => onPaperChange("a4")}>A4</button>
        </div>
        <a className="button button-dark" href={pdfHref} target="_blank" rel="noreferrer"><Printer /> Open / Print PDF</a>
        <a className="button button-primary" href={pdfHref} download><Download /> Download PDF</a>
      </div>
    </div>
  );
}

function BriefFooter({ path, label }: { path: string; label: string }) {
  const destination = `${PUBLIC_SITE_URL}${path}`;
  const displayDestination = destination.replace(/^https?:\/\//, "");
  return (
    <footer className="brief-footer">
      <div><strong>Start with one useful pilot.</strong><span>{label}</span><b>{displayDestination}</b></div>
      <DeferredQRCode value={destination} size={62} level="M" title={`QR code for ${destination}`} />
    </footer>
  );
}

type AudienceGuide = {
  path: string;
  role: PartnerInquiryRole;
  eyebrow: string;
  title: string;
  emphasis: string;
  introduction: string;
  icon: LucideIcon;
  tension: string;
  storyGoal?: JourneyGoalId;
  pains: Array<{ title: string; body: string; icon: LucideIcon }>;
  pilot: Array<{ title: string; body: string }>;
  organizationBenefits: string[];
  communityBenefits: string[];
  safeguards: string[];
  signal: string;
  sectionCopy?: Partial<{
    painKicker: string;
    painTitle: string;
    painBody: string;
    pilotKicker: string;
    pilotTitle: string;
    pilotBody: string;
    valueKicker: string;
    valueTitle: string;
    valueBody: string;
    guardrailKicker: string;
    guardrailTitle: string;
  }>;
};

const audienceGuides: Record<string, AudienceGuide> = {
  "/partners/workplaces": {
    path: "/partners/workplaces",
    role: "employer_partner",
    eyebrow: "Workplaces & facilities",
    title: "Retire\u00a0assets,",
    emphasis: "not their usefulness.",
    introduction: "Furniture, devices, equipment, and supplies can outlive a workplace refresh. A bounded GreenSwap pilot can help eligible items leave storage through approved, destination-aware handoffs instead of an improvised giveaway.",
    icon: Factory,
    tension: "A workplace needs control, while useful assets need a responsible path out.",
    storyGoal: "organization",
    pains: [
      { title: "Storage becomes the default", body: "Retired items accumulate because disposition takes coordination across facilities, IT, finance, and community teams.", icon: Boxes },
      { title: "Eligibility is not obvious", body: "Ownership, data, safety, warranty, tax, and asset-policy questions must be resolved before anything can move.", icon: ShieldCheck },
      { title: "Good intent becomes ad hoc", body: "Informal employee giveaways can create fairness, custody, transport, and recordkeeping problems.", icon: Users },
      { title: "Impact is easy to overstate", body: "Leaving a storeroom is not the same as reaching a useful destination. Confirmation matters.", icon: BadgeCheck },
    ],
    pilot: [
      { title: "Choose one eligible batch", body: "Start with one site, one refresh, and categories the organization is authorized to release." },
      { title: "Clear the release gates", body: "Document ownership, data handling, condition, safety, recipients, transport, and fallback routes." },
      { title: "Invite the right community", body: "Offer approved items through employees, a named community, or qualified local partners." },
      { title: "Confirm the destination", body: "Record completed custody transfers and report only what the evidence supports." },
    ],
    organizationBenefits: ["A repeatable disposition path", "Less unmanaged storage", "Clear staff and partner roles", "Evidence matched to actual handoffs"],
    communityBenefits: ["Access to useful equipment and supplies", "Fairer, clearer participation", "Local organizations receive eligible offers, not speculative ones", "More items begin another useful chapter"],
    safeguards: ["No data-bearing device moves without the organization’s approved sanitization process.", "GreenSwap does not decide legal, accounting, tax, safety, or asset-policy eligibility.", "Unclaimed or ineligible items keep an approved fallback route.", "Public reporting separates confirmed transfers from estimates."],
    signal: "One workplace · one eligible asset batch · one accountable handoff window",
  },
  "/partners/schools-colleges": {
    path: "/partners/schools-colleges",
    role: "education_leader",
    eyebrow: "Schools, colleges & institutes",
    title: "Keep campus resources",
    emphasis: "in circulation.",
    introduction: "Move-out weeks, program changes, lab refreshes, and rotating student leadership can strand useful resources. GreenSwap can help one education community make approved items, needs, and handoffs easier to discover.",
    icon: GraduationCap,
    tension: "Useful resources are scattered across a campus while students and programs still need them.",
    pains: [
      { title: "Turnover happens all at once", body: "Dorm move-outs, graduations, and semester transitions compress decisions into a short, stressful window.", icon: Clock3 },
      { title: "Resources stay invisible", body: "Art supplies, STEM parts, books, furniture, and club equipment live in separate rooms, chats, and spreadsheets.", icon: Search },
      { title: "Access can be uneven", body: "Students and educators may buy items that already exist elsewhere in the same learning community.", icon: BookOpen },
      { title: "Ownership needs clarity", body: "Institution-owned, grant-funded, club-owned, and personally owned items cannot follow the same rules.", icon: ShieldCheck },
    ],
    pilot: [
      { title: "Select one community", body: "Begin with one residence, department, makerspace, school, club network, or move-out period." },
      { title: "Define eligible resources", body: "Separate personal, institutional, restricted, unsafe, and approved categories before launch." },
      { title: "Publish offers and needs", body: "Give participants one place to find approved resources and express real program needs." },
      { title: "Learn from confirmed use", body: "Track participation and completed handoffs without exposing private student information." },
    ],
    organizationBenefits: ["Less last-minute resource confusion", "Continuity when student leaders rotate", "Clear ownership and stewardship", "Program-level learning without public student data"],
    communityBenefits: ["Lower barriers to books, gear, and supplies", "More value from existing campus resources", "A welcoming way for new students to participate", "Practical reuse connected to learning"],
    safeguards: ["The institution defines eligibility, access, safety, and supervision rules.", "Protected student and education records do not belong in public listings or pilot reporting.", "Restricted, hazardous, recalled, or sensitive items stay outside the pilot.", "Any public outcome uses aggregate, privacy-safe evidence."],
    signal: "One campus community · one transition window · one approved resource loop",
    sectionCopy: {
      painKicker: "The campus transition gap",
      painTitle: "Campus transitions should not erase useful value.",
      painBody: "Move-outs, program changes, and leadership turnover create distinct pressures. The first pilot should validate where one education community loses visibility, continuity, or access.",
      pilotKicker: "One accountable campus loop",
      pilotTitle: "Start with one transition and clear stewards.",
      pilotBody: "Choose one campus community, separate ownership classes, name responsible stewards, and make only approved resources discoverable.",
      valueKicker: "Continuity and access",
      valueTitle: "Help programs remember and more students participate.",
      valueBody: "A useful campus pilot preserves resource knowledge through leadership changes while lowering barriers to books, supplies, gear, and learning opportunities.",
      guardrailKicker: "Different owners, different rules",
      guardrailTitle: "Campus circulation needs clear stewardship.",
    },
  },
  "/partners/public-agencies": {
    path: "/partners/public-agencies",
    role: "public_agency",
    eyebrow: "Cities & public agencies",
    title: "Prevent useful goods from becoming",
    emphasis: "public cleanup.",
    introduction: "Residents often see a curb, hallway, or collection area as the last available option. A GreenSwap pilot can add an earlier, community-based route while preserving local collection rules and public-service responsibilities.",
    icon: Globe2,
    tension: "Residents need a simple next step before useful goods enter the cleanup system.",
    pains: [
      { title: "The public sees fragmented options", body: "Reuse organizations, bulky pickup, repair, donation, and disposal rules are often spread across different programs.", icon: Route },
      { title: "Timing defeats good intent", body: "A resident may have only days to move an item, while partner intake windows and transportation remain limited.", icon: Clock3 },
      { title: "Cleanup absorbs the failure", body: "When earlier routes fail, property teams, public works, haulers, and neighborhoods inherit the result.", icon: Recycle },
      { title: "Claims need public trust", body: "Program reporting must distinguish a listing, a claimed item, a confirmed handoff, and an estimated outcome.", icon: BadgeCheck },
    ],
    pilot: [
      { title: "Choose one place and moment", body: "Focus on a neighborhood, multifamily corridor, seasonal move-out, or another bounded pressure point." },
      { title: "Map approved routes", body: "Connect existing agency guidance, property rules, collection services, and qualified reuse partners." },
      { title: "Offer an earlier action", body: "Give residents a clear chance to list, match, or route eligible items before the cleanup deadline." },
      { title: "Report with restraint", body: "Measure confirmed activity, name gaps, and avoid attributing outcomes GreenSwap cannot verify." },
    ],
    organizationBenefits: ["One visible pilot boundary", "Better connection to existing services", "A shared language for partner roles", "Privacy-safe program evidence"],
    communityBenefits: ["Clearer next steps before disposal", "More local access to useful goods", "Less confusion around timing and rules", "Local reuse partners become easier to find"],
    safeguards: ["GreenSwap complements official collection, safety, enforcement, and emergency channels. It does not replace them.", "Local law, agency guidance, and property rules control every pilot.", "No item waits in an unapproved public or shared space.", "The pilot keeps a time-bound fallback for anything not matched."],
    signal: "One service area · one recurring pressure point · one earlier route",
    sectionCopy: {
      painKicker: "Before the curb becomes the answer",
      painTitle: "Residents need an earlier route than public cleanup.",
      painBody: "A useful public pilot begins where timing, fragmented guidance, and limited transport leave residents without a practical next step before collection or enforcement takes over.",
      pilotKicker: "One public pressure point",
      pilotTitle: "Connect one clear message to accountable local routes.",
      pilotBody: "Choose one recurring place or moment, map the official and community options, and give residents a time-bound earlier action with an approved fallback.",
      valueKicker: "Clearer public service",
      valueTitle: "Make the next step easier for residents and service teams.",
      valueBody: "The agency retains authority while residents gain a simpler path to reuse, repair, collection, or another locally approved option.",
      guardrailKicker: "Official rules remain authoritative",
      guardrailTitle: "A reuse option must respect public collection rules.",
    },
  },
  "/partners/reuse-organizations": {
    path: "/partners/reuse-organizations",
    role: "nonprofit_partner",
    eyebrow: "Reuse & donation organizations",
    title: "Receive what you need, not what you must",
    emphasis: "dispose of.",
    introduction: "Donation is useful only when the receiving organization can accept, process, and place the item. GreenSwap can help publish current needs and acceptance rules before a donor loads the car.",
    icon: HandHeart,
    tension: "A hopeful donation can still create labor, storage, and disposal work for the recipient.",
    storyGoal: "services",
    pains: [
      { title: "Offers do not match current need", body: "Condition, dimensions, category, timing, and client needs can turn a generous offer into unusable intake.", icon: Gift },
      { title: "Acceptance changes quickly", body: "Capacity, season, staffing, warehouse space, and program demand make a static donation list unreliable.", icon: RefreshCw },
      { title: "Intake consumes scarce time", body: "Teams answer repeat questions, inspect unsuitable goods, and coordinate transportation before the mission work begins.", icon: Clock3 },
      { title: "Donors need a respectful no", body: "When an item is not suitable, people need a clear alternative without the impression that every offer should be accepted.", icon: HeartHandshake },
    ],
    pilot: [
      { title: "Publish live acceptance rules", body: "Name needed categories, condition, dimensions, timing, service area, and exclusions." },
      { title: "Qualify before intake", body: "Collect enough item information for the organization, rather than GreenSwap, to decide whether to proceed." },
      { title: "Coordinate the handoff", body: "Use an approved appointment, drop-off, pickup, or referral process with named responsibility." },
      { title: "Close the loop", body: "Confirm accepted transfers and give declined offers a responsible alternative where possible." },
    ],
    organizationBenefits: ["Better-qualified offers", "Less avoidable intake work", "Current needs become discoverable", "Organization-controlled acceptance decisions"],
    communityBenefits: ["Know what is useful before traveling", "Understand why some items cannot be accepted", "Find another route when available", "Support organizations without shifting disposal work onto them"],
    safeguards: ["The receiving organization always controls acceptance.", "A referral is not a promise of pickup, placement, tax treatment, or client use.", "Sensitive client needs and recipient identities remain private.", "GreenSwap reports accepted and confirmed activity, not the intent to donate."],
    signal: "One receiving partner · one current needs list · one qualified intake loop",
    sectionCopy: {
      painKicker: "Good intent needs a real fit",
      painTitle: "Good donations begin with a qualified match.",
      painBody: "The most respectful intake experience protects the organization’s capacity before an item travels, while helping donors understand what is useful now and what needs another route.",
      pilotKicker: "Qualify before intake",
      pilotTitle: "Publish needs and acceptance rules before items move.",
      pilotBody: "Start with one receiving partner and a current needs list, then collect enough information for that organization to accept, decline, or redirect each offer.",
      valueKicker: "Mission time protected",
      valueTitle: "Reduce sorting work while helping suitable items arrive.",
      valueBody: "The organization controls every acceptance decision. Donors gain clarity, and staff spend more time placing useful items instead of managing preventable intake.",
      guardrailKicker: "A respectful no is part of the service",
      guardrailTitle: "Acceptance belongs to the receiving organization.",
    },
  },
  "/partners/tool-libraries": {
    path: "/partners/tool-libraries",
    role: "nonprofit_partner",
    eyebrow: "Tool libraries & lending programs",
    title: "Grow access without replacing",
    emphasis: "what already works.",
    introduction: "Tool libraries already have trusted catalogues, lending rules, member records, and checkout workflows. GreenSwap can complement that foundation with a community-facing discovery and referral layer, without asking the library to start over in another system.",
    icon: Library,
    tension: "A strong lending operation can still be hard for new members, donors, volunteers, and sponsors to discover and support.",
    pains: [
      { title: "Discovery stops at the catalogue", body: "People may not know the library exists, understand who can join, or see how borrowing lowers the cost of trying a new skill.", icon: Search },
      { title: "Requests live outside the system", body: "Unmet needs, donation offers, volunteer interest, and community questions arrive through email, chat, forms, and word of mouth.", icon: MessageCircleHeart },
      { title: "Useful offers need qualification", body: "A donated tool may be unsafe, redundant, hard to maintain, or outside the library’s collection policy even when the intent is generous.", icon: ShieldCheck },
      { title: "Community value is hard to show", body: "Loans and memberships are recorded, but access stories, unmet demand, referrals, and partner-supported outcomes may remain fragmented.", icon: BadgeCheck },
    ],
    pilot: [
      { title: "Choose one workflow to augment", body: "Start with discovery, qualified donations, unmet-demand signals, newcomer onboarding, or community referrals rather than duplicating the whole catalogue." },
      { title: "Keep the library system authoritative", body: "Reservations, member status, waivers, checkout, returns, fees, maintenance, and safety rules remain in the library’s existing system." },
      { title: "Add a GreenSwap front door", body: "Explain the offer in community language, collect only the information the library wants, and route each next action to the approved workflow." },
      { title: "Review evidence together", body: "Measure qualified referrals, completed next steps, unmet needs, and staff effort before considering any deeper integration." },
    ],
    organizationBenefits: ["More discoverable programs and membership paths", "Better-qualified tool and equipment offers", "Visible unmet demand without speculative purchasing", "Sponsor and volunteer conversations grounded in evidence"],
    communityBenefits: ["Lower upfront cost to try a skill or hobby", "A clearer path from interest to membership", "Access to trusted equipment and guidance", "More ways to lend, donate, volunteer, or champion the library"],
    safeguards: ["The library’s existing system remains the source of truth for inventory, availability, members, reservations, waivers, and lending records.", "GreenSwap does not promise that an item is available, safe, accepted, or suitable for a particular use.", "The library controls collection policy, eligibility, training, maintenance, fees, and every lending decision.", "Any future data exchange requires explicit agreement, minimum necessary fields, and a separate technical review."],
    signal: "One tool library · one workflow to augment · one referral loop",
  },
  "/partners/repair-organizations": {
    path: "/partners/repair-organizations",
    role: "nonprofit_partner",
    eyebrow: "Repair organizations & programs",
    title: "Turn repair interest into",
    emphasis: "prepared requests.",
    introduction: "Repair cafés, fix-it clinics, community workshops, and service organizations need enough information to route an item responsibly before it arrives. GreenSwap can help prepare the request and connect people to the organization’s existing booking, event, or intake workflow.",
    icon: Wrench,
    tension: "People want to save useful things, while repair teams need clear scope, capacity, parts, skills, and safety boundaries.",
    pains: [
      { title: "The item arrives before the facts", body: "Teams may not know the model, condition, failure, previous repair attempts, parts needs, or safety concerns until intake begins.", icon: Clipboard },
      { title: "Capacity changes constantly", body: "Volunteer skills, event schedules, benches, parts, and appointment availability determine what can be accepted on a given day.", icon: Clock3 },
      { title: "Not every item is repairable", body: "Unsafe, recalled, contaminated, restricted, or uneconomical items need a respectful fallback rather than an implied repair promise.", icon: ShieldCheck },
      { title: "Completed care is hard to trace", body: "Interest, intake, diagnosis, repair, reuse, and responsible end-of-life are different outcomes and should not be reported as one result.", icon: Route },
    ],
    pilot: [
      { title: "Define one repair pathway", body: "Choose one event, item category, service area, or recurring intake flow with explicit exclusions and capacity." },
      { title: "Prepare the request", body: "Collect the item identity, symptoms, photos, ownership, timing, and relevant safety questions before routing it onward." },
      { title: "Return to the existing workflow", body: "Booking, queueing, diagnosis, estimates, consent, parts, payment, and repair records stay with the repair organization." },
      { title: "Confirm the actual outcome", body: "Distinguish referral, accepted intake, completed repair, continued use, and responsible fallback in the pilot review." },
    ],
    organizationBenefits: ["Better-prepared requests before intake", "Fewer unsuitable or out-of-scope arrivals", "Clearer visibility into recurring needs and skills", "Evidence for volunteers, funders, and community partners"],
    communityBenefits: ["Know what information to gather before seeking help", "Find an appropriate repair path sooner", "Understand limits without losing the next responsible option", "Keep useful items in service when a qualified repair is possible"],
    safeguards: ["The repair organization controls acceptance, diagnosis, safety, estimates, consent, parts, service terms, and completion records.", "GreenSwap does not diagnose items, certify repairs, quote prices, promise outcomes, or replace professional safety judgment.", "Hazardous, recalled, contaminated, data-bearing, or otherwise restricted items follow the organization’s approved policy.", "A referral or intake request is not reported as a completed repair or extended useful life."],
    signal: "One repair program · one eligible item category · one prepared intake loop",
  },
};

function CorporateDeviceReuseProgram({ onOpenInquiry }: { onOpenInquiry: (role?: string) => void }) {
  const stages = [
    { title: "Approve", body: "The employer confirms ownership, eligibility, and release authority." },
    { title: "Sanitize", body: "The company’s approved IT or asset-disposition process removes data and records completion." },
    { title: "Refurbish", body: "A qualified partner tests condition, battery health, accessories, and software readiness." },
    { title: "Match", body: "A verified nonprofit, school, or library program defines the devices it can use and support." },
    { title: "Confirm", body: "The parties document custody transfer, delivery, and the approved route for devices that fail." },
  ];
  return (
    <section className="technology-reuse-section section" aria-label="Corporate technology reuse">
      <div className="shell">
        <SectionIntro kicker="Corporate technology reuse" title="Retire devices, not their usefulness." body="Route approved laptops through secure data handling, professional refurbishment, and verified community programs. GreenSwap coordinates the path while the employer and qualified specialists retain control of every security and release decision." />

        <div className="technology-story-grid">
          <figure>
            <picture><source srcSet="/assets/corporate-device-reuse-before-v2.webp" type="image/webp" /><img src="/assets/corporate-device-reuse-before-v2.png" width="1536" height="1024" loading="lazy" alt="An IT asset manager reviews a distinctive charcoal laptop with a green inventory square in an IT storage and quality-check room while a technician tests another device in the background." /></picture>
            <figcaption><strong>Useful, but waiting</strong><span>Retired laptops stay in secure storage while ownership, sanitization, condition, and destination remain unresolved.</span></figcaption>
          </figure>
          <figure>
            <picture><source srcSet="/assets/corporate-device-reuse-after.webp" type="image/webp" /><img src="/assets/corporate-device-reuse-after.png" width="1536" height="1024" loading="lazy" alt="The same IT asset manager hands the same charcoal laptop with its green inventory square and diagonal scuff to an adult nonprofit technology-program coordinator while students learn in the background." /></picture>
            <figcaption><strong>Prepared and placed</strong><span>The same laptop reaches a verified community technology program through a documented handoff.</span></figcaption>
          </figure>
        </div>

        <ol className="technology-release-chain" aria-label="Secure device reuse chain">
          {stages.map(({ title, body }, index) => <li key={title}><span>0{index + 1}</span><strong>{title}</strong><p>{body}</p></li>)}
        </ol>

        <div className="technology-role-grid">
          <article><Building2 /><strong>Employer and IT</strong><p>Control ownership, policy, data handling, device eligibility, and final release.</p></article>
          <article><ShieldCheck /><strong>Refurbisher or IT asset partner</strong><p>Sanitize, test, grade, prepare, and responsibly recycle devices that do not qualify.</p></article>
          <article><HandHeart /><strong>Community program</strong><p>Define specifications and capacity, accept eligible devices, and support participants.</p></article>
          <article><Route /><strong>GreenSwap</strong><p>Catalogue approved availability, support matching, coordinate the handoff, and preserve evidence.</p></article>
        </div>

        <div className="technology-pilot-callout">
          <div><p>GreenSwap coordinates. Specialists certify.</p><h3>Start with one device refresh and one verified recipient.</h3><span>Report devices approved, sanitized, refurbished, delivered, and responsibly recycled. Delivery alone does not prove student, educational, or employment outcomes.</span></div>
          <button className="button button-primary" type="button" onClick={() => onOpenInquiry("employer_partner")}>Talk with GreenSwap <ArrowRight /></button>
        </div>
      </div>
    </section>
  );
}

function WorkplaceHeroVisual() {
  return (
    <figure className="workplace-hero-visual">
      <picture>
        <source srcSet="/assets/workplace-device-reuse-hero.webp" type="image/webp" />
        <img
          src="/assets/workplace-device-reuse-hero.png"
          width="1672"
          height="941"
          loading="eager"
          fetchPriority="high"
          draggable={false}
          alt="A student uses and appreciates a refurbished charcoal laptop at a community technology program while a mentor looks on with quiet pride."
        />
      </picture>
    </figure>
  );
}

function WorkplaceConversationStarter({ onOpenInquiry }: { onOpenInquiry: (role?: string) => void }) {
  return (
    <section className="workplace-conversation-section section" aria-labelledby="workplace-conversation-title">
      <div className="shell">
        <SectionIntro
          kicker="Help an idea travel"
          title="Give an internal champion something credible to forward."
          body="Lead with one useful outcome, keep control with the responsible teams, and ask for a small first conversation rather than a company-wide commitment."
        />
        <div className="workplace-conversation-layout">
          <blockquote>
            <span>Conversation starter</span>
            <p>Could we explore one small pilot for an approved batch of retired laptops? Our teams would keep control of security, data sanitization, ownership, and release. GreenSwap would help us identify a qualified community technology partner and confirm each handoff before we expand.</p>
          </blockquote>
          <div className="workplace-conversation-checklist">
            <h3>Bring four facts to the first conversation.</h3>
            <ol>
              <li><span>01</span><p><strong>The opportunity</strong>What useful devices or supplies are sitting idle or approaching refresh?</p></li>
              <li><span>02</span><p><strong>The people</strong>Who could put them to meaningful use?</p></li>
              <li><span>03</span><p><strong>The owners</strong>Who controls security, assets, release, and community relationships?</p></li>
              <li><span>04</span><p><strong>The smallest test</strong>What single batch, site, or refresh could prove the workflow?</p></li>
            </ol>
            <button className="button button-dark" type="button" onClick={() => onOpenInquiry("employer_partner")}>Prepare my workplace introduction <ArrowRight /></button>
          </div>
        </div>
      </div>
    </section>
  );
}

type ComplementaryGuideKind = "tool-library" | "repair";

function ComplementarySystemConversationKit({ kind, onOpenInquiry }: { kind: ComplementaryGuideKind; onOpenInquiry: (role?: string) => void }) {
  const isToolLibrary = kind === "tool-library";
  const content = isToolLibrary
    ? {
        kicker: "Conversation kit for tool libraries",
        title: "Add a community front door around the system you trust.",
        body: "Begin with the library’s current workflow and one growth or service gap. GreenSwap should earn a place by reducing friction at the edge, not by recreating the operational core.",
        adds: ["Community discovery and qualified referrals", "Unmet-demand and donation-interest signals", "Newcomer, volunteer, and sponsor pathways", "A shared pilot recap based on confirmed actions"],
        stays: ["Inventory and availability", "Membership and eligibility", "Reservations, checkout, and returns", "Waivers, training, fees, and maintenance"],
        quote: "We already have a lending system that our team relies on. Could we test GreenSwap as a community discovery and referral layer for one workflow, while keeping our catalogue, membership, reservations, waivers, and checkout process exactly where they are?",
        questions: ["Which current system and workflows are essential?", "Where do staff or volunteers repeat manual work?", "Which people or offers are hardest to qualify?", "What would make a small pilot clearly worthwhile?"],
        relatedHref: "/partners/repair-organizations",
        relatedLabel: "Also operate a repair program? Open the separate repair guide",
      }
    : {
        kicker: "Conversation kit for repair organizations",
        title: "Prepare the request, then respect the repair workflow.",
        body: "GreenSwap can help people arrive with better information and a realistic next step. The repair organization keeps control of capacity, safety, diagnosis, service terms, and the final outcome.",
        adds: ["A guided pre-intake request", "Current scope, exclusions, and capacity signals", "Routing to events, booking, or approved fallbacks", "Outcome evidence that separates interest from repair"],
        stays: ["Diagnosis and safety decisions", "Appointments, queues, and consent", "Estimates, parts, payment, and warranties", "Repair notes and completion records"],
        quote: "Could we test a GreenSwap pre-intake path for one item category or repair event? People would gather the information you need before they arrive, then continue through your existing booking, diagnosis, safety, and service workflow.",
        questions: ["Which requests consume the most triage time?", "What information is essential before intake?", "Which skills, parts, and capacity change most often?", "What outcomes can the organization responsibly confirm?"],
        relatedHref: "/partners/tool-libraries",
        relatedLabel: "Also lend tools or equipment? Open the separate tool-library guide",
      };

  return (
    <section className="complementary-kit-section section" aria-labelledby={`${kind}-kit-title`}>
      <div className="shell">
        <SectionIntro kicker={content.kicker} title={content.title} body={content.body} />
        <div className="complementary-boundary-grid">
          <article>
            <span>GreenSwap can add</span>
            <ul>{content.adds.map((item) => <li key={item}><Check /> {item}</li>)}</ul>
          </article>
          <article>
            <span>The current system keeps</span>
            <ul>{content.stays.map((item) => <li key={item}><ShieldCheck /> {item}</li>)}</ul>
          </article>
        </div>
        <div className="complementary-conversation-grid">
          <blockquote>
            <span>Forwardable conversation starter</span>
            <p>{content.quote}</p>
          </blockquote>
          <div>
            <h3 id={`${kind}-kit-title`}>Bring four facts to the first conversation.</h3>
            <ol>{content.questions.map((question, index) => <li key={question}><span>0{index + 1}</span><p>{question}</p></li>)}</ol>
            <div className="complementary-kit-actions">
              <button className="button button-dark" type="button" onClick={() => onOpenInquiry("nonprofit_partner")}>Prepare my introduction <ArrowRight /></button>
              <a href={content.relatedHref}>{content.relatedLabel} <ArrowRight /></a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AudienceGuidePage({ guide, onOpenInquiry }: { guide: AudienceGuide; onOpenInquiry: (role?: string) => void }) {
  const HeroIcon = guide.icon;
  const titleWords = guide.title.split(" ");
  const titleBridge = titleWords.pop() || "";
  const titleLead = titleWords.join(" ");
  const storyGoal = guide.storyGoal
    ? journeyGoals.find((goal): goal is JourneyStoryGoal => goal.id === guide.storyGoal && goal.visual !== "community_access")
    : undefined;
  return (
    <main id="main" className="partner-site partner-detail audience-guide-page">
      <PageMeta path={guide.path} title={`${guide.eyebrow} | GreenSwap`} description={guide.introduction} />
      <section className={`partner-detail-hero audience-detail-hero${guide.path === "/partners/workplaces" ? " workplace-detail-hero" : ""}`} id="top">
        <div className="shell detail-hero-grid">
          <div>
            <a className="back-link" href="/partners"><ChevronLeft /> All partner guides</a>
            <p className="section-kicker">{guide.eyebrow}</p>
            <h1>{guide.path === "/partners/workplaces" ? <>{guide.title}{"\u00a0"}<em>{guide.emphasis}</em></> : <>{titleLead && `${titleLead} `}<span className="hero-bridge">{titleBridge}{"\u00a0"}<em>{guide.emphasis}</em></span></>}</h1>
            <p>{guide.introduction}</p>
            <div className="detail-hero-actions"><a className="button button-primary" href="#pilot">See how this pilot could work <ArrowDown /></a><button className="button button-glass" onClick={() => onOpenInquiry(guide.role)}>Talk with GreenSwap</button></div>
          </div>
          {guide.path === "/partners/workplaces"
            ? <WorkplaceHeroVisual />
            : storyGoal
            ? <StoryVisual goal={storyGoal} className="role-story-visual--detail" />
            : <aside className="detail-story-card audience-story-card"><span>The tension</span><HeroIcon /><strong>{guide.tension}</strong><p>The first conversation validates this pain locally before GreenSwap proposes a workflow.</p><div><span>Pain</span><ArrowRight /><span>Bounded pilot</span><ArrowRight /><span>Evidence</span></div></aside>}
        </div>
      </section>

      <section className="audience-pain-section section" aria-labelledby="audience-pain-title"><div className="shell"><SectionIntro kicker={guide.sectionCopy?.painKicker ?? "What gets in the way"} title={guide.sectionCopy?.painTitle ?? "Good intentions still need an operating path."} body={guide.sectionCopy?.painBody ?? "These are starting hypotheses, not claims about every organization. A responsible pilot begins by validating which pressures are real in one local setting."} /><div className="operating-grid">{guide.pains.map(({ title, body, icon: Icon }) => <article key={title}><Icon /><h3>{title}</h3><p>{body}</p></article>)}</div></div></section>

      {guide.path === "/partners/workplaces" && <CorporateDeviceReuseProgram onOpenInquiry={onOpenInquiry} />}
      {guide.path === "/partners/workplaces" && <WorkplaceConversationStarter onOpenInquiry={onOpenInquiry} />}
      {guide.path === "/partners/tool-libraries" && <ComplementarySystemConversationKit kind="tool-library" onOpenInquiry={onOpenInquiry} />}
      {guide.path === "/partners/repair-organizations" && <ComplementarySystemConversationKit kind="repair" onOpenInquiry={onOpenInquiry} />}

      <section className="audience-pilot-section section" id="pilot" aria-labelledby="audience-pilot-title"><div className="shell audience-pilot-layout"><div><p className="section-kicker">{guide.sectionCopy?.pilotKicker ?? "A small first loop"}</p><h2 id="audience-pilot-title">{guide.sectionCopy?.pilotTitle ?? "Prove the workflow before expanding the promise."}</h2><p>{guide.sectionCopy?.pilotBody ?? "GreenSwap and the partner define the boundary, owners, safeguards, evidence, and fallback before inviting participants."}</p><button className="button button-dark" onClick={() => onOpenInquiry(guide.role)}>Talk with GreenSwap <ArrowRight /></button></div><ol>{guide.pilot.map(({ title, body }, index) => <li key={title}><span>0{index + 1}</span><div><strong>{title}</strong><p>{body}</p></div></li>)}</ol></div></section>

      <section className="audience-value-section section" aria-labelledby="audience-value-title"><div className="shell"><SectionIntro light kicker={guide.sectionCopy?.valueKicker ?? "Value on both sides"} title={guide.sectionCopy?.valueTitle ?? "Help the organization and the people it serves."} body={guide.sectionCopy?.valueBody ?? "A pilot is worth running only when it reduces friction for the partner and creates a clearer, more useful experience for participants."} /><div className="audience-value-grid"><article><span>For the organization</span><ul>{guide.organizationBenefits.map((benefit) => <li key={benefit}><Check /> {benefit}</li>)}</ul></article><article><span>For the community</span><ul>{guide.communityBenefits.map((benefit) => <li key={benefit}><Check /> {benefit}</li>)}</ul></article></div></div></section>

      <section className="audience-guardrail-section section" aria-labelledby="audience-guardrail-title"><div className="shell audience-guardrail-layout"><div><p className="section-kicker">{guide.sectionCopy?.guardrailKicker ?? "Non-negotiable guardrails"}</p><h2 id="audience-guardrail-title">{guide.sectionCopy?.guardrailTitle ?? "Useful does not mean unmanaged."}</h2></div><div>{guide.safeguards.map((safeguard) => <p key={safeguard}><ShieldCheck /> {safeguard}</p>)}</div></div><div className="shell operating-cta"><p><strong>A credible starting boundary:</strong> {guide.signal}</p><button className="button button-dark" onClick={() => onOpenInquiry(guide.role)}>Talk with GreenSwap <ArrowRight /></button></div></section>
    </main>
  );
}

function PartnerHub({ onOpenInquiry }: { onOpenInquiry: (role?: string) => void }) {
  const [guideFilter, setGuideFilter] = useState<"all" | PartnerGuideCategory>("all");
  const guideFilters: Array<{ id: "all" | PartnerGuideCategory; label: string }> = [
    { id: "all", label: "All guides" },
    { id: "communities", label: "Housing & groups" },
    { id: "institutions", label: "Workplaces, schools & cities" },
    { id: "reuse", label: "Reuse & repair" },
    { id: "sponsors", label: "Sponsors" },
  ];
  const visibleGuides = partnerGuideInventory.filter((guide) => guide.status === "ready" && (guideFilter === "all" || guide.category === guideFilter));
  return (
    <main id="main" className="partner-site">
      <PageMeta title="Get involved | GreenSwap" description="Bring GreenSwap to a community you care about with practical guides for housing, clubs, employers, schools, public agencies, reuse organizations, tool libraries, and repair programs." />
      <section className="field-hero" id="top">
        <div className="shell field-hero-inner">
          <p className="eyebrow eyebrow-light"><span>Get involved</span></p>
          <h1><span className="hero-phrase" style={{ whiteSpace: "nowrap" }}>Bring GreenSwap</span> to a community <span style={{ whiteSpace: "nowrap" }}>you{"\u00a0"}<em>care about.</em></span></h1>
          <p>Whether you organize a community, manage a property, provide reuse services, support employees, or see a way to make participation easier, there is a practical place to begin.</p>
          <div className="field-hero-actions"><a className="button button-primary" href="#ready-guides">Choose a starting point <ArrowDown /></a><button className="button button-glass" onClick={() => onOpenInquiry()}>Talk with GreenSwap</button></div>
        </div>
      </section>

      <section className="field-featured section" id="ready-guides" aria-labelledby="ready-guides-title">
        <div className="shell">
          <SectionIntro kicker="Choose a way to get involved" title="Start with a problem people already feel." body="Short, practical guides you can share with a colleague, community leader, or potential partner. You do not need a formal proposal or decision-making authority to begin." />
          <div className="partner-guide-filters" role="group" aria-label="Filter partner guides">
            {guideFilters.map(({ id, label }) => <button key={id} type="button" aria-pressed={guideFilter === id} onClick={() => setGuideFilter(id)}>{label}</button>)}
          </div>
          <div className="featured-guide-grid">
            {visibleGuides.map(({ title, audience, promise, badge, thought, href, icon: Icon }, index) => (
              <a className={`featured-guide featured-guide-${index + 1}`} href={href} key={title} aria-label={`Open the ${title} guide`}>
                <div className="featured-guide-top"><span>0{index + 1}</span><span>{badge}</span></div>
                <Icon />
                <p>{audience}</p>
                <blockquote className="featured-guide-thought"><span aria-hidden="true">•••</span>{thought}</blockquote>
                <h2>{promise}</h2>
                <strong>Open the {title.toLowerCase()} guide <ArrowRight /></strong>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="field-principles section" aria-labelledby="field-principles-title">
        <div className="shell field-principles-layout">
          <SectionIntro light kicker="What working together looks like" title="Start small. Learn honestly. Build from there." body="GreenSwap begins with the workflow and community you already know, then helps define a practical pilot with clear responsibilities, safeguards, and evidence." />
          <div className="field-principle-list">
            {["Begin with the problem your team or community already handles.", "Keep your existing systems, rules, and responsible people in control.", "Agree who stores, moves, inspects, lends, repairs, and receives eligible items.", "Report confirmed activity clearly, with estimates and limitations labeled.", "Make the pilot useful for staff, volunteers, residents, and members."].map((item, index) => <div key={item}><span>0{index + 1}</span><p>{item}</p></div>)}
          </div>
        </div>
      </section>
    </main>
  );
}

function HousingBrief({ paper }: { paper: PaperSize }) {
  return (
    <article className={`print-sheet print-sheet--${paper} housing-sheet`} aria-labelledby="housing-brief-title">
      <header className="brief-head"><PrintBrand /><div><span>Partner Conversation Brief</span><strong>Housing communities</strong></div></header>
      <div className="brief-hero-grid">
        <div className="brief-title-block">
          <p>Move-out should not become clean-up.</p>
          <h2 id="housing-brief-title">Turn move-out waste into <em>move-in value.</em></h2>
          <span>Give residents an earlier, easier route for useful furniture, appliances, and household goods - before they reach the trash room or curb.</span>
        </div>
        <HousingStoryVisual variant="brief" />
      </div>
      <section className="brief-pain" aria-labelledby="housing-pain-title">
        <div className="brief-section-label"><span>01</span><h3 id="housing-pain-title">The pain you already manage</h3></div>
        <div className="brief-card-row brief-card-row-3">
          <div><Clock3 /><strong>Move-out pressure</strong><p>Residents run out of time and leave bulky, useful items behind.</p></div>
          <div><Building2 /><strong>Property burden</strong><p>Teams coordinate removal, answer complaints, and restore common areas.</p></div>
          <div><Recycle /><strong>Value becomes waste</strong><p>Working items reach hauling and disposal before another person can find them.</p></div>
        </div>
      </section>
      <section className="brief-flow" aria-labelledby="housing-flow-title">
        <div className="brief-section-label"><span>02</span><h3 id="housing-flow-title">A practical GreenSwap pilot</h3></div>
        <ol><li><b>Invite early</b><span>Share the move-out route before the final week.</span></li><li><b>List safely</b><span>Eligible items stay with their owner - not in a hallway.</span></li><li><b>Confirm a handoff</b><span>A neighbor or qualified organization accepts the item.</span></li><li><b>Use the fallback</b><span>Unclaimed items follow the property’s approved removal process.</span></li></ol>
      </section>
      <section className="brief-benefits" aria-labelledby="housing-benefits-title">
        <div className="brief-section-label"><span>03</span><h3 id="housing-benefits-title">Value on both sides of the door</h3></div>
        <div className="brief-benefit-grid"><div><strong>For property teams</strong><ul><li>Fewer abandoned-item interventions</li><li>Cleaner common areas</li><li>A clearer resident process</li><li>Pilot-level outcome reporting</li></ul></div><div><strong>For residents</strong><ul><li>Less last-minute move-out stress</li><li>Simple local handoffs</li><li>Access to useful household goods</li><li>A better alternative to a “Free” pile</li></ul></div></div>
      </section>
      <section className="brief-proof"><div><span>Pilot signals</span><strong>Confirmed handoffs</strong><strong>Staff interventions</strong><strong>Unclaimed items</strong><strong>Resident feedback</strong></div><p><ShieldCheck /> Pilot proposal - not a guarantee of cost savings or waste diversion. Local rules, item eligibility, safety, and property policies govern every handoff.</p></section>
      <BriefFooter path="/partners/housing" label="Suggested start: one property | one move-out window | one measurable test" />
    </article>
  );
}

const partnerInquiryRoles = [
  { id: "property_manager", label: "Property or community manager", note: "On-site operations or leasing", icon: Building2 },
  { id: "regional_leader", label: "Regional management leader", note: "Portfolio or multi-property oversight", icon: Building2 },
  { id: "owner_hoa", label: "Property owner or HOA board", note: "Ownership or community governance", icon: Home },
  { id: "facilities_sustainability", label: "Facilities or sustainability", note: "Operations, waste, or impact programs", icon: Recycle },
  { id: "resident_champion", label: "Resident champion", note: "Bring the idea to your community", icon: HeartHandshake },
  { id: "education_leader", label: "School, college, or institute leader", note: "Campus, district, or program leadership", icon: GraduationCap },
  { id: "school_champion", label: "School or campus champion", note: "Student, parent, educator, or staff advocate", icon: BookOpen },
  { id: "community_group", label: "Community group or club", note: "Social, hobby, sports, maker, or volunteer group", icon: Users },
  { id: "employer_partner", label: "Employer or workplace partner", note: "Device reuse, employee sharing, or surplus programs", icon: Factory },
  { id: "public_agency", label: "City or public agency", note: "Public works, libraries, or sustainability", icon: Globe2 },
  { id: "nonprofit_partner", label: "Nonprofit or reuse partner", note: "Donation, repair, or community service", icon: HandHeart },
  { id: "corporate_sponsor", label: "Corporate sponsor", note: "Fund or support a local pilot", icon: CircleDollarSign },
  { id: "other", label: "Another community partner", note: "Tell us how you would like to help", icon: Users },
] as const;

type PartnerInquiryRole = (typeof partnerInquiryRoles)[number]["id"];

const inquiryRoleAliases: Record<string, PartnerInquiryRole> = {
  "Apartment or property manager": "property_manager",
  "Community organization": "other",
  "Community group or club": "community_group",
  "Corporate sponsor": "corporate_sponsor",
  "City or school": "public_agency",
  "Repair organization": "nonprofit_partner",
  "Reuse nonprofit": "nonprofit_partner",
  "Tool library": "nonprofit_partner",
};

function resolveInquiryRole(value?: string | null): PartnerInquiryRole | undefined {
  if (!value) return undefined;
  if (partnerInquiryRoles.some((role) => role.id === value)) return value as PartnerInquiryRole;
  return inquiryRoleAliases[value];
}

function PartnerInquiryForm({ initialRole, sourcePage, returnHref = "/partners", embedded = false }: { initialRole?: string; sourcePage: string; returnHref?: string; embedded?: boolean }) {
  const sectionRef = useRef<HTMLElement>(null);
  const resolvedInitialRole = resolveInquiryRole(initialRole);
  const [roles, setRoles] = useState<PartnerInquiryRole[]>(resolvedInitialRole ? [resolvedInitialRole] : []);
  const [showRoleDirectory, setShowRoleDirectory] = useState(!resolvedInitialRole);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [organizationName, setOrganizationName] = useState("");
  const [location, setLocation] = useState("");
  const [message, setMessage] = useState("");
  const [propertyScale, setPropertyScale] = useState("");
  const [currentProcess, setCurrentProcess] = useState("");
  const [institutionType, setInstitutionType] = useState("");
  const [decisionMakerContact, setDecisionMakerContact] = useState("");
  const [serviceArea, setServiceArea] = useState("");
  const [sponsorshipInterest, setSponsorshipInterest] = useState("");
  const [website, setWebsite] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "saved" | "error">("idle");
  const [error, setError] = useState("");
  const [reference, setReference] = useState("");

  const hasHousingRole = roles.some((role) => ["property_manager", "regional_leader", "owner_hoa", "facilities_sustainability", "resident_champion"].includes(role));
  const hasEducationRole = roles.some((role) => role === "education_leader" || role === "school_champion");
  const hasChampionRole = roles.some((role) => role === "resident_champion" || role === "school_champion");
  const hasNonprofitRole = roles.includes("nonprofit_partner");
  const hasSponsorRole = roles.includes("corporate_sponsor");
  const hasEmployerRole = roles.includes("employer_partner") || roles.includes("facilities_sustainability");
  const hasCommunityGroupRole = roles.includes("community_group");
  const championOnly = roles.length > 0 && roles.every((role) => role === "resident_champion" || role === "school_champion");

  useEffect(() => {
    if (embedded) return;
    const frame = window.requestAnimationFrame(() => sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
    return () => window.cancelAnimationFrame(frame);
  }, [embedded]);

  const toggleRole = (role: PartnerInquiryRole) => {
    setRoles((current) => current.includes(role) ? current.filter((item) => item !== role) : [...current, role]);
    setStatus("idle");
    setError("");
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (roles.length === 0) {
      setStatus("error");
      setError("Choose at least one role so we can route your inquiry to the right conversation.");
      return;
    }
    setStatus("sending");
    setError("");
    try {
      const response = await reliabilityFetch(`${API_BASE_URL}/public/partner-inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contactName,
          contactEmail,
          organizationName,
          location,
          roles,
          message,
          website,
          sourcePage,
          details: { propertyScale, currentProcess, institutionType, decisionMakerContact, serviceArea, sponsorshipInterest },
        }),
      }, "partner_inquiries");
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.error || "We could not send your inquiry. Please try again.");
      setReference(result.reference || "");
      setStatus(result.notificationDelivered === false ? "saved" : "sent");
    } catch (caught) {
      setStatus("error");
      setError(caught instanceof Error ? caught.message : "We could not send your inquiry. Please try again.");
    }
  };

  const submitLabel = hasSponsorRole
    ? "Start the conversation"
    : championOnly
      ? "Nominate my community"
      : "Request a pilot conversation";
  const emailSubject = encodeURIComponent(`GreenSwap community introduction: ${organizationName || "community pilot"}`);
  const emailBody = encodeURIComponent([
    `Name: ${contactName}`,
    `Email: ${contactEmail}`,
    `Organization or community: ${organizationName}`,
    `Location: ${location || "Not provided"}`,
    `Roles: ${roles.map((role) => partnerInquiryRoles.find((item) => item.id === role)?.label ?? role).join(", ")}`,
    "",
    "What I would like GreenSwap to help make possible:",
    message,
  ].join("\n"));
  const emailFallbackHref = `mailto:${PARTNER_INQUIRY_EMAIL}?subject=${emailSubject}&body=${emailBody}`;

  return (
    <section ref={sectionRef} className="housing-pilot-inquiry section" id="housing-pilot-inquiry" aria-labelledby="housing-pilot-inquiry-title">
      <div className="shell inquiry-inline-shell">
        <div className="inquiry-inline-intro">
          <p className="section-kicker">Let’s find the right starting point</p>
          <h2 id="housing-pilot-inquiry-title">The <span className="inquiry-nowrap">GreenSwap team</span> would love to connect with the <em>person best placed to help.</em></h2>
          <p>Tell us how you are connected. Choose every role that applies, and we’ll shape the conversation around your community instead of forcing everyone through the same form.</p>
          <div className="inquiry-expectation"><HeartHandshake /><div><strong>Champions are welcome.</strong><span>You can introduce a property, school, college, university, or institute even if you do not have a decision-maker’s contact yet.</span></div></div>
          <div className="inquiry-expectation"><Clock3 /><div><strong>Expect a human response.</strong><span>We aim to review pilot and sponsorship introductions within three business days.</span></div></div>
          <div className="inquiry-expectation"><ShieldCheck /><div><strong>Your inquiry is recorded first.</strong><span>GreenSwap keeps the reference in its system, then sends a notification to info@greenswap.us for follow-up.</span></div></div>
          {!embedded && <a className="text-button inquiry-dismiss" href={returnHref}>Not ready yet. Return to the site</a>}
        </div>

        {status === "sent" || status === "saved" ? (
          <div className="inquiry-success" aria-live="polite">
            <div className="ready-icon">{status === "sent" ? <Check /> : <Clipboard />}</div>
            <p className="section-kicker">{status === "sent" ? "Introduction received" : "Introduction saved"}</p>
            <h3>{status === "sent" ? "Thank you for opening the door." : "Your introduction is safely recorded."}</h3>
            <p>{status === "sent"
              ? "The GreenSwap team will review your introduction and aims to follow up within three business days with the most relevant next step."
              : IS_LOCAL_PREVIEW
                ? "Your introduction was saved in the local preview, but email delivery is not configured here. Open the prepared email below so the GreenSwap team receives it."
                : "Your introduction was saved, but email delivery could not be confirmed. Please use the prepared email below so the GreenSwap team receives it."}</p>
            {reference && <span>Reference {reference.slice(0, 8).toUpperCase()}</span>}
            {status === "saved" && <a className="button button-dark" href={emailFallbackHref}>Open prepared email <ArrowRight /></a>}
            <a className={`button ${status === "sent" ? "button-dark" : "button-quiet"}`} href={returnHref}>Return to the site</a>
          </div>
        ) : (
          <form className="inquiry-inline-form" onSubmit={submit}>
            <fieldset className="inquiry-role-fieldset">
              <legend><span>1</span> How are you connected?</legend>
              {roles.length > 0 && !showRoleDirectory ? (
                <div className="inquiry-selected-roles">
                  <div>{roles.map((role) => {
                    const option = partnerInquiryRoles.find((item) => item.id === role);
                    if (!option) return null;
                    const Icon = option.icon;
                    return <span key={role}><Icon /><strong>{option.label}</strong></span>;
                  })}</div>
                  <button type="button" onClick={() => setShowRoleDirectory(true)}>Add another role</button>
                </div>
              ) : (
                <>
                  <p>Select all that apply.</p>
                  <div className="inquiry-role-grid">
                    {partnerInquiryRoles.map(({ id, label, note, icon: Icon }) => {
                      const selected = roles.includes(id);
                      return <button key={id} type="button" className={selected ? "is-selected" : ""} aria-pressed={selected} onClick={() => toggleRole(id)}><Icon /><span><strong>{label}</strong><small>{note}</small></span>{selected && <Check className="role-check" />}</button>;
                    })}
                  </div>
                  {resolvedInitialRole && <button className="inquiry-role-done" type="button" disabled={roles.length === 0} onClick={() => setShowRoleDirectory(false)}>Done choosing roles</button>}
                </>
              )}
            </fieldset>

            <fieldset className="inquiry-details-fieldset">
              <legend><span>2</span> Where should we begin?</legend>
              <div className="inquiry-field-grid">
                <label>Your name<input required value={contactName} onChange={(event) => setContactName(event.target.value)} autoComplete="name" /></label>
                <label>Email<input required type="email" value={contactEmail} onChange={(event) => setContactEmail(event.target.value)} autoComplete="email" /></label>
                <label>Organization, property, school, or community<input required value={organizationName} onChange={(event) => setOrganizationName(event.target.value)} autoComplete="organization" placeholder="A name is enough. You can be the champion" /></label>
                <label>City and state <span>(optional)</span><input value={location} onChange={(event) => setLocation(event.target.value)} autoComplete="address-level2" placeholder="Oakland, CA" /></label>
              </div>

              <label className="inquiry-message">What would you like GreenSwap to help make possible?<textarea required value={message} onChange={(event) => setMessage(event.target.value)} rows={5} placeholder={hasEmployerRole ? "Example: We retire about 40 laptops each year. IT controls sanitization and release, and we would like to explore one verified community technology partner." : hasCommunityGroupRole ? "Example: New hikers often need a suitable pack and poles before their first outing. Experienced members may have safe starter gear they could lend." : hasNonprofitRole ? "Example: Our lending catalogue and checkout system work well. We would like to test a better way for new members to discover us and for staff to qualify donation offers." : "Share the pain point, the community you care about, or the pilot you would like to explore."} /></label>
              {(hasHousingRole || hasEducationRole || hasEmployerRole || hasCommunityGroupRole || hasChampionRole || hasNonprofitRole || hasSponsorRole) && <details className="inquiry-optional-context">
                <summary>Add helpful context <span>(optional)</span></summary>
                {hasHousingRole && <div className="inquiry-tailored-block"><p>Housing community context</p><div className="inquiry-field-grid"><label>Approximate property or portfolio size <span>(optional)</span><input value={propertyScale} onChange={(event) => setPropertyScale(event.target.value)} placeholder="About 220 homes or 4 properties" /></label><label>What happens to unwanted items today? <span>(optional)</span><textarea value={currentProcess} onChange={(event) => setCurrentProcess(event.target.value)} rows={3} placeholder="Trash room, curb, hauling process, resident notices…" /></label></div></div>}
                {hasEducationRole && <div className="inquiry-tailored-block"><p>School, college, or institute context</p><div className="inquiry-field-grid"><label>What kind of education community? <span>(optional)</span><select value={institutionType} onChange={(event) => setInstitutionType(event.target.value)}><option value="">Choose one</option><option>K–12 school or district</option><option>College or university</option><option>Trade school or training institute</option><option>Research or learning institute</option><option>Student organization</option><option>Other education community</option></select></label></div></div>}
                {hasEmployerRole && <div className="inquiry-tailored-block inquiry-tailored-block--workplace"><p>Workplace conversation starter</p><div className="inquiry-guidance-list"><span>What useful resources are sitting idle or approaching refresh?</span><span>Who controls security, ownership, release, and community relationships?</span><span>Who could benefit, and what single batch or site could make a responsible first test?</span></div></div>}
                {hasCommunityGroupRole && <div className="inquiry-tailored-block inquiry-tailored-block--community"><p>Community group conversation starter</p><div className="inquiry-guidance-list"><span>What activity brings your group together?</span><span>Which starter equipment creates the biggest barrier for newcomers?</span><span>Who could offer suitable gear, and who would help steward lending, fit, maintenance, or safe use?</span></div></div>}
                {hasChampionRole && <div className="inquiry-tailored-block inquiry-tailored-block--champion"><p>Optional introduction help</p><label>Is there someone we should invite into the conversation? <span>(optional)</span><textarea value={decisionMakerContact} onChange={(event) => setDecisionMakerContact(event.target.value)} rows={2} placeholder="A role or name is helpful. Leave blank if you do not know yet." /></label></div>}
                {hasNonprofitRole && <div className="inquiry-tailored-block"><p>Reuse, repair, or tool-library context</p><label>Service area, current system, item categories, or community need <span>(optional)</span><textarea value={serviceArea} onChange={(event) => setServiceArea(event.target.value)} rows={3} placeholder="Tell us what system or workflow already works well, and where you would like GreenSwap to complement it." /></label></div>}
                {hasSponsorRole && <div className="inquiry-tailored-block"><p>Sponsorship context</p><label>What community or outcome might your company support? <span>(optional)</span><textarea value={sponsorshipInterest} onChange={(event) => setSponsorshipInterest(event.target.value)} rows={3} /></label></div>}
              </details>}
              <label className="inquiry-honeypot" aria-hidden="true">Website<input tabIndex={-1} autoComplete="off" value={website} onChange={(event) => setWebsite(event.target.value)} /></label>
              {status === "error" && <div className="inquiry-error" role="alert"><strong>{error}</strong><span>Your information is still in the form. You can try again or <a href={emailFallbackHref}>email the GreenSwap team instead</a>.</span></div>}
              <div className="inquiry-submit-row"><button className="button button-dark" type="submit" disabled={status === "sending"}>{status === "sending" ? "Sending…" : submitLabel} <ArrowRight /></button><p>By submitting, you agree that GreenSwap may contact you about this inquiry. Your details are not published.</p></div>
            </fieldset>
          </form>
        )}
      </div>
    </section>
  );
}

function HousingPage({ onOpenInquiry }: { onOpenInquiry: (role?: string) => void }) {
  const [paper, setPaper] = useState<PaperSize>("letter");
  return (
    <main id="main" className="partner-site partner-detail">
      <PageMeta title="Housing Partner Guide | GreenSwap" description="A practical GreenSwap pilot brief for apartment and residential property teams managing useful items during move-out." />
      <style media="print">{`@page { size: ${paper === "a4" ? "A4" : "Letter"} portrait; margin: 0; }`}</style>
      <section className="partner-detail-hero housing-detail-hero" id="top">
        <div className="shell detail-hero-grid"><div><a className="back-link" href="/partners"><ChevronLeft /> All partner guides</a><p className="section-kicker">Housing communities</p><h1>Turn move-out waste <span style={{ whiteSpace: "nowrap" }}>into{"\u00a0"}<em>move-in value.</em></span></h1><p>Help residents act before useful items become a property problem. The proposed pilot combines early communication, owner-held listings, confirmed handoffs, and a clear fallback.</p><div className="detail-hero-actions"><button className="button button-primary" onClick={() => document.getElementById("housing-brief")?.scrollIntoView({ behavior: "smooth" })}>View the one-page guide <ArrowDown /></button><button className="button button-glass" onClick={() => onOpenInquiry("property_manager")}>Talk with GreenSwap</button></div></div><HousingStoryVisual variant="hero" /></div>
      </section>
      <section className="brief-preview section" id="housing-brief"><BriefToolbar guide="housing" paper={paper} onPaperChange={setPaper} /><div className="sheet-stage"><HousingBrief paper={paper} /></div></section>
      <section className="guide-source-section section"><div className="shell source-layout"><div><p className="section-kicker">Grounded, then localized</p><h2>The problem is real. The rules are local.</h2></div><div><p>Public guidance documents furniture, appliances, computers, and other bulky goods as common dumping challenges. Some communities place timely-removal responsibilities or costs on property owners. A GreenSwap pilot must use the property’s approved storage and disposal rules rather than replace them.</p><div className="source-links"><a href="https://calrecycle.ca.gov/illegaldump/prevention/" target="_blank" rel="noreferrer">CalRecycle prevention guidance <ArrowRight /></a><a href="https://www.gainesvillefl.gov/Government-Pages/Government/Departments/Public-Works/Garbage-Recycling/Businesses-Apartments/Apartment-Recycling" target="_blank" rel="noreferrer">Gainesville multifamily guidance <ArrowRight /></a><a href="https://www.corvallisoregon.gov/publicworks/page/recycling-corvallis" target="_blank" rel="noreferrer">Corvallis reuse and recycling guidance <ArrowRight /></a></div><button className="button button-dark" onClick={() => onOpenInquiry("property_manager")}>Talk with GreenSwap <ArrowRight /></button></div></div></section>
    </main>
  );
}

function CommunityBrief({ paper }: { paper: PaperSize }) {
  return (
    <article className={`print-sheet print-sheet--${paper} community-sheet`} aria-labelledby="community-brief-title">
      <header className="brief-head"><PrintBrand /><div><span>Partner Conversation Brief</span><strong>Community groups & clubs</strong></div></header>
      <div className="brief-title-block">
        <p>Lower the cost of joining in.</p>
        <h2 id="community-brief-title">Help more people join with what your group <em>already has.</em></h2>
        <span>Make starter gear and supplies easier to discover, borrow, trade, or receive, while giving the group a shared memory beyond chats and spreadsheets.</span>
      </div>
      <section className="brief-pain" aria-labelledby="community-pain-title">
        <div className="brief-section-label"><span>01</span><h3 id="community-pain-title">The hidden-resource problem</h3></div>
        <div className="brief-card-row brief-card-row-3"><div><Boxes /><strong>Invisible inventory</strong><p>Useful gear and materials sit scattered across homes and storage cabinets.</p></div><div><MessageCircleHeart /><strong>Requests disappear</strong><p>“Does anyone have…?” gets buried in chat, email, and old spreadsheets.</p></div><div><Users /><strong>Cost blocks participation</strong><p>New members may have to buy specialized items before they know what fits.</p></div></div>
      </section>
      <section className="brief-community-models" aria-labelledby="community-models-title">
        <div className="brief-section-label"><span>02</span><h3 id="community-models-title">One shared memory, many kinds of groups</h3></div>
        <div className="community-model-grid"><div><BookOpen /><strong>Exchange circles</strong><span>Books, games, cards</span></div><div><Bike /><strong>Gear circles</strong><span>Outdoor & sports</span></div><div><ToolCase /><strong>Maker circles</strong><span>STEM, DIY, woodworking</span></div><div><Sparkles /><strong>Creative circles</strong><span>Art, sewing, gardening</span></div></div>
      </section>
      <section className="brief-benefits" aria-labelledby="community-benefits-title">
        <div className="brief-section-label"><span>03</span><h3 id="community-benefits-title">What GreenSwap helps the group do</h3></div>
        <div className="brief-benefit-grid"><div><strong>For organizers</strong><ul><li>Make shared resources discoverable</li><li>Publish needs and suitable donations</li><li>Preserve knowledge when leaders change</li><li>Run swap, lending, or repair events</li></ul></div><div><strong>For members</strong><ul><li>Participate without buying everything</li><li>Find relevant items in a trusted circle</li><li>Pass materials to people who value them</li><li>Build connection through useful exchange</li></ul></div></div>
      </section>
      <section className="brief-proof"><div><span>Example pilot</span><strong>20 members</strong><strong>25 resources</strong><strong>5 shared needs</strong><strong>1 community event</strong></div><p><ShieldCheck /> Shared does not automatically mean safe. Groups define membership, visibility, inspection, training, and eligibility - especially for power tools, youth activities, and safety-critical gear.</p></section>
      <BriefFooter path="/partners/community-groups" label="Suggested start: one group | twenty members | one useful event" />
    </article>
  );
}

function CommunityGroupsPage({ onOpenInquiry }: { onOpenInquiry: (role?: string) => void }) {
  const [paper, setPaper] = useState<PaperSize>("letter");
  return (
    <main id="main" className="partner-site partner-detail">
      <PageMeta title="Community Groups Partner Guide | GreenSwap" description="A GreenSwap pilot brief for clubs, social groups, outdoor communities, sports teams, and maker organizations." />
      <style media="print">{`@page { size: ${paper === "a4" ? "A4" : "Letter"} portrait; margin: 0; }`}</style>
      <section className="partner-detail-hero community-detail-hero" id="top"><div className="shell detail-hero-grid"><div><a className="back-link" href="/partners"><ChevronLeft /> All partner guides</a><p className="section-kicker">Community groups & clubs</p><h1>Make more possible with what your <span style={{ whiteSpace: "nowrap" }}>group{"\u00a0"}<em>already has.</em></span></h1><p>Books, games, camping gear, tools, robotics components, and creative supplies already live throughout the community. GreenSwap can give the group a shared memory for what it owns, needs, lends, repairs, and passes forward.</p><div className="detail-hero-actions"><button className="button button-primary" onClick={() => document.getElementById("community-brief")?.scrollIntoView({ behavior: "smooth" })}>View the one-page guide <ArrowDown /></button><button className="button button-glass" onClick={() => onOpenInquiry("Community group or club")}>Talk with GreenSwap</button></div></div><div className="community-constellation" aria-label="Examples of community resources"><div><BookOpen /><span>Books</span></div><div><Bike /><span>Outdoor gear</span></div><div><ToolCase /><span>Tools</span></div><div><Boxes /><span>STEM parts</span></div><div><Sparkles /><span>Creative supplies</span></div></div></div></section>
      <section className="brief-preview section" id="community-brief"><BriefToolbar guide="community-groups" paper={paper} onPaperChange={setPaper} /><div className="sheet-stage"><CommunityBrief paper={paper} /></div></section>
      <section className="community-operating-section section"><div className="shell"><SectionIntro kicker="A group is more than an inventory" title="Trust needs rules it can remember." body="Informal clubs and formal organizations can begin differently, but both need clear stewardship, visibility, safety, and leadership handoff." /><div className="operating-grid">{[{title:"Ownership",body:"Distinguish member-owned, group-owned, and donated resources.",icon:PackageCheck},{title:"Visibility",body:"Choose public discovery, member-only sharing, or private inventory.",icon:Search},{title:"Stewardship",body:"Assign who can approve, inspect, lend, and retire group resources.",icon:ShieldCheck},{title:"Continuity",body:"Keep shared knowledge with the group when organizers rotate.",icon:RefreshCw}].map(({title,body,icon:Icon})=><article key={title}><Icon /><h3>{title}</h3><p>{body}</p></article>)}</div><div className="operating-cta"><p><strong>Start small:</strong> one group, twenty members, twenty-five resources, five needs, and one useful event.</p><button className="button button-dark" onClick={() => onOpenInquiry("Community group or club")}>Talk with GreenSwap <ArrowRight /></button></div></div></section>
    </main>
  );
}

function SponsorBrief({ paper }: { paper: PaperSize }) {
  return (
    <article className={`print-sheet print-sheet--${paper} sponsor-sheet`} aria-labelledby="sponsor-brief-title">
      <header className="brief-head"><PrintBrand /><div><span>Sponsor Conversation Brief</span><strong>Community pilot sponsorship</strong></div></header>
      <div className="brief-title-block sponsor-brief-title">
        <p>Fund useful local capacity.</p>
        <h2 id="sponsor-brief-title">Help local reuse grow with evidence people can <em>trust.</em></h2>
        <span>GreenSwap designs bounded community pilots around a real local problem, a named delivery partner, and reporting that separates confirmed outcomes from estimates.</span>
      </div>
      <section className="brief-pain" aria-labelledby="sponsor-programs-title">
        <div className="brief-section-label"><span>01</span><h3 id="sponsor-programs-title">Choose a concrete program</h3></div>
        <div className="brief-card-row brief-card-row-3"><div><Wrench /><strong>Repair access</strong><p>Support partner capacity, events, parts, and resident access.</p></div><div><ToolCase /><strong>Shared tools</strong><p>Support useful inventory, maintenance, and neighborhood discovery.</p></div><div><Building2 /><strong>Workplace surplus</strong><p>Route eligible equipment through responsible, confirmed handoffs.</p></div></div>
      </section>
      <section className="brief-benefits" aria-labelledby="sponsor-model-title">
        <div className="brief-section-label"><span>02</span><h3 id="sponsor-model-title">Set the pilot up responsibly</h3></div>
        <div className="brief-benefit-grid"><div><strong>A pilot begins with</strong><ul><li>A community problem and bounded scope</li><li>Roles, safeguards, and fallback routes</li><li>Measures and evidence categories before launch</li><li>A plain-language pilot recap</li></ul></div><div><strong>Public claims stay within the evidence</strong><ul><li>Confirmed activity is separated from estimates</li><li>No automatic diversion, emissions, or offset claims</li><li>No guaranteed participation or cost savings</li><li>Sponsor credit uses a documented method</li></ul></div></div>
      </section>
      <section className="brief-proof sponsor-proof"><div><span>Evidence may include</span><strong>Confirmed handoffs</strong><strong>Partner-confirmed activity</strong><strong>Participation signals</strong><strong>Named methodology</strong></div><p><ShieldCheck /> Every public claim names its evidence, assumptions, and limitations. Sponsor recognition follows the agreed program role and reporting method.</p></section>
      <footer className="brief-footer sponsor-brief-footer"><div><strong>Start with one bounded community pilot.</strong><span>Pilot sponsors receive collaborative planning, launch support, and a documented recap.</span><b>{PUBLIC_SITE_URL.replace(/^https?:\/\//, "")}/sponsors</b></div><div className="brief-qr"><DeferredQRCode value={`${PUBLIC_SITE_URL}/sponsors`} size={112} level="M" title={`QR code for ${PUBLIC_SITE_URL}/sponsors`} /><span>Explore the sponsor brief</span></div></footer>
    </article>
  );
}

function SponsorPage({ onOpenInquiry }: { onOpenInquiry: (role?: string) => void }) {
  const [paper, setPaper] = useState<PaperSize>("letter");
  return (
    <main id="main" className="partner-site sponsor-site">
      <PageMeta path="/sponsors" title="Community Impact Partners | GreenSwap" description="Sponsor a bounded local reuse pilot with GreenSwap and receive reporting that names the evidence, methodology, assumptions, and limitations." />
      <style media="print">{`@page { size: ${paper === "a4" ? "A4" : "Letter"} portrait; margin: 0; }`}</style>
      <section className="sponsor-page-hero" id="top"><div className="shell sponsor-page-grid"><div><a className="back-link" href="/"><ChevronLeft /> GreenSwap home</a><p className="eyebrow eyebrow-light"><span>Community Impact Partners</span></p><h1>Sponsor a local reuse economy, <em>not an ad.</em></h1><p>Choose a concrete community problem. Help trusted local partners deliver a bounded pilot. Receive an honest account of what happened, what was measured, and what remains uncertain.</p><div className="detail-hero-actions"><button className="button button-primary" onClick={() => onOpenInquiry("corporate_sponsor")}>Talk with GreenSwap <ArrowRight /></button><button className="button button-glass" onClick={() => document.getElementById("sponsor-brief")?.scrollIntoView({ behavior: "smooth" })}>View the one-page guide <ArrowDown /></button></div></div><div className="sponsor-promise-card"><ShieldCheck /><p>GreenSwap sponsorship principle</p><strong>Fund useful local capacity. Report evidence without borrowing credit from the community.</strong><span>No vague green claims. No offsets. No outcome attribution without a documented method.</span></div></div></section>
      <section className="sponsor-program-section section"><div className="shell"><SectionIntro kicker="Three starting points" title="Put support behind a visible local job." body="The first conversation chooses the community problem and delivery partners before it chooses the campaign language." /><div className="sponsor-program-grid">{sponsorPrograms.map(({label,title,description,proof,icon:Icon})=><article key={label}><Icon /><p>{label}</p><h2>{title}</h2><span>{description}</span><div>{proof.map(item=><small key={item}><Check /> {item}</small>)}</div></article>)}</div></div></section>
      <section className="sponsor-agreement section"><div className="shell sponsor-agreement-grid"><div><p className="section-kicker">What a founding pilot includes</p><h2>A clear agreement before the first claim.</h2><p>GreenSwap and the participating community define the pilot boundary, responsibilities, safeguards, evidence, and fallback before launch.</p></div><ol><li><span>01</span><div><strong>Choose the local problem</strong><p>Repair access, shared tools, workplace surplus, or another qualified use case.</p></div></li><li><span>02</span><div><strong>Name delivery and safeguards</strong><p>Identify who owns, stores, inspects, moves, and receives eligible items.</p></div></li><li><span>03</span><div><strong>Agree on evidence</strong><p>Separate measured, participant-reported, and modeled information.</p></div></li><li><span>04</span><div><strong>Publish an honest recap</strong><p>Report outcomes, assumptions, limitations, and what the community learned.</p></div></li></ol></div></section>
      <section className="brief-preview section" id="sponsor-brief"><BriefToolbar guide="sponsorship" paper={paper} onPaperChange={setPaper} /><div className="sheet-stage"><SponsorBrief paper={paper} /></div></section>
      <section className="sponsor-final section"><div className="shell"><div><p className="section-kicker">Founding pilot conversations</p><h2>Start with one community and one useful outcome.</h2><p>Accepted pilot partners receive collaborative planning, practical launch support, and preferred future pricing. Participation and public claims remain subject to a written pilot scope.</p></div><button className="button button-dark" onClick={() => onOpenInquiry("corporate_sponsor")}>Talk with GreenSwap <ArrowRight /></button></div></section>
    </main>
  );
}

function PublicPosterToolbar({ paper, finish, onPaperChange, onFinishChange }: { paper: PaperSize; finish: PosterFinish; onPaperChange: (paper: PaperSize) => void; onFinishChange: (finish: PosterFinish) => void }) {
  const pdfHref = `/downloads/greenswap-public-starter-poster-${paper}-${finish}.pdf`;
  return (
    <div className="brief-toolbar poster-toolbar shell" aria-label="Public poster controls">
      <div>
        <p className="section-kicker">Public Starter Poster Kit</p>
        <p>Choose the format that fits your notice board and printer.</p>
      </div>
      <div className="brief-controls poster-controls">
        <div className="paper-switch" role="group" aria-label="Paper size">
          <button type="button" aria-pressed={paper === "letter"} onClick={() => onPaperChange("letter")}>US Letter</button>
          <button type="button" aria-pressed={paper === "a4"} onClick={() => onPaperChange("a4")}>A4</button>
        </div>
        <div className="paper-switch" role="group" aria-label="Print finish">
          <button type="button" aria-pressed={finish === "color"} onClick={() => onFinishChange("color")}>Full color</button>
          <button type="button" aria-pressed={finish === "ink"} onClick={() => onFinishChange("ink")}>Ink-friendly</button>
        </div>
        <a className="button button-dark" href={pdfHref} target="_blank" rel="noreferrer"><Printer /> Open / Print PDF</a>
        <a className="button button-primary" href={pdfHref} download><Download /> Download PDF</a>
      </div>
    </div>
  );
}

function PublicStarterPoster({ paper, finish }: { paper: PaperSize; finish: PosterFinish }) {
  const destination = `${PUBLIC_SITE_URL}/start?source=public-poster`;
  return (
    <article className={`public-poster public-poster--${paper} public-poster--${finish}`} aria-labelledby="public-poster-title">
      <header className="public-poster-head"><PrintBrand /><span>Share locally. Keep useful things moving.</span></header>
      <div className="public-poster-visual">
        <div className="public-poster-photo">
          <picture>
            <source srcSet="/assets/hero-life-beyond-the-shelf.webp" type="image/webp" />
            <img src="/assets/hero-life-beyond-the-shelf.png" alt="A person carries a useful lamp toward an open front door, ready for its next life." width="1792" height="1024" draggable={false} />
          </picture>
        </div>
        <div className="public-poster-message">
          <p>Make room for what matters.</p>
          <h1 id="public-poster-title">Make space.<br /><em>Keep useful things moving.</em></h1>
          <span>Catalogue what you have, find it when you need it, and give unused items a new life through lending, trading, or giving.</span>
        </div>
      </div>
      <ol className="public-poster-steps">
        <li><span>01</span><div><strong>Catalogue your things</strong><p>Know what is in closets, boxes, and storage.</p></div></li>
        <li><span>02</span><div><strong>Free up your space</strong><p>See what you use and what is ready to move on.</p></div></li>
        <li><span>03</span><div><strong>Give things their next life</strong><p>Lend, trade, or give within communities you trust.</p></div></li>
      </ol>
      <div className="public-poster-trust"><ShieldCheck /><span>You decide what stays private and when an item is ready to circulate.</span></div>
      <footer className="public-poster-footer">
        <div><p>Start your GreenSwap journey</p><strong>greenswap.us/start</strong><span>Scan to see what your useful things can do next.</span></div>
        <DeferredQRCode value={destination} size={150} level="M" marginSize={2} title={`QR code for ${destination}`} />
      </footer>
    </article>
  );
}

function PublicStartPage() {
  const [paper, setPaper] = useState<PaperSize>("letter");
  const [finish, setFinish] = useState<PosterFinish>("color");
  return (
    <main id="main" className="public-start-page">
      <PageMeta path="/start" title="Start Your GreenSwap Journey" description="Catalogue what you have, free up your space, and give useful items their next life with GreenSwap." />
      <style media="print">{`@page { size: ${paper === "a4" ? "A4" : "Letter"} portrait; margin: 0; }`}</style>
      <section className="public-start-hero">
        <div className="shell public-start-hero-grid">
          <div><p className="section-kicker">Your useful things still have places to go</p><h1>Make space. <em>Keep useful things moving.</em></h1><p>Catalogue what you have, find it when you need it, and give unused items a new life when you are ready.</p><div><a className="button button-primary" href={BROWSE_URL}>Explore GreenSwap items <ArrowRight /></a><a className="button button-glass" href="#poster-kit">Print a public poster <ArrowDown /></a></div></div>
          <picture><source srcSet="/assets/hero-life-beyond-the-shelf.webp" type="image/webp" /><img src="/assets/hero-life-beyond-the-shelf.png" alt="A person carries a useful lamp toward an open front door, ready for its next life." width="1792" height="1024" /></picture>
        </div>
      </section>
      <section className="public-start-steps section" aria-labelledby="public-start-steps-title"><div className="shell"><SectionIntro kicker="Begin with what you already own" title="See it. Make room. Pass it forward." body="GreenSwap helps useful things become visible before they become clutter, forgotten purchases, or waste." /><h2 className="sr-only" id="public-start-steps-title">How to start with GreenSwap</h2><div className="public-start-step-grid"><article><Boxes /><span>01</span><h3>Catalogue your things</h3><p>Remember what is in closets, boxes, garages, and storage.</p></article><article><Search /><span>02</span><h3>Find what you need</h3><p>Locate what you already own before buying it again.</p></article><article><RefreshCw /><span>03</span><h3>Circulate when ready</h3><p>Lend, trade, or give useful items within communities you trust.</p></article></div><div className="public-start-trust"><ShieldCheck /><p><strong>Your things stay yours.</strong> You decide what stays private, who can see an item, and when it is ready to move.</p></div></div></section>
      <NextLifeGuide />
      <section className="poster-preview section" id="poster-kit"><PublicPosterToolbar paper={paper} finish={finish} onPaperChange={setPaper} onFinishChange={setFinish} /><div className="poster-stage"><PublicStarterPoster paper={paper} finish={finish} /></div></section>
    </main>
  );
}

function ConnectPage() {
  const params = new URLSearchParams(window.location.search);
  const initialRole = params.get("role") ?? undefined;
  const sourcePage = params.get("source") || "/connect";
  const returnHref = sourcePage.startsWith("/") && !sourcePage.startsWith("//") ? sourcePage : "/partners";
  return (
    <main id="main" className="connect-page">
      <PageMeta path="/connect" title="Start a GreenSwap Conversation" description="Introduce a community, propose a pilot, or explore responsible sponsorship with the GreenSwap team." />
      <section className="connect-hero"><div className="shell"><p className="eyebrow eyebrow-light"><span>Start a conversation</span></p><h1>Bring the problem your community <em>already{"\u00a0"}feels.</em></h1><p>GreenSwap will help identify the smallest responsible pilot worth exploring - or tell you honestly when another route is a better fit.</p></div></section>
      <PartnerInquiryForm initialRole={initialRole} sourcePage={sourcePage} returnHref={returnHref} />
    </main>
  );
}

type TrustPageKey = "about" | "privacy" | "terms" | "accessibility";

const trustPages: Record<TrustPageKey, { title: string; eyebrow: string; description: string; intro: string; sections: Array<{ title: string; body: string }> }> = {
  about: { title: "Useful things deserve another chapter.", eyebrow: "About GreenSwap", description: "Learn why GreenSwap exists and how it works with communities to keep useful items in motion.", intro: "GreenSwap is building local infrastructure for giving, trading, lending, repairing, and responsibly routing useful things - in partnership with the organizations communities already trust.", sections: [{ title: "The mission", body: "Help every useful thing find its next life and fulfill more of its purpose." }, { title: "The operating principle", body: "Partnerships over competition. GreenSwap should make local organizations easier to discover, support, and participate in - not replace their work." }, { title: "The evidence principle", body: "Mission before metrics. Evidence before claims. Public reporting must distinguish measured, participant-reported, and modeled information." }, { title: "The current stage", body: "Neighbor-to-neighbor circulation is available in the GreenSwap app. Partner programs, service connections, and sponsor-supported pilots are being developed in bounded, transparent stages." }] },
  privacy: {
    title: "Privacy should be understandable.",
    eyebrow: "Website privacy",
    description: "How the GreenSwap public website handles partnership inquiries, essential reliability signals, privacy choices, and optional analytics.",
    intro: "The public website collects only what is needed to operate reliably, understand journeys when permission is granted, and respond to partnership, pilot, or sponsorship inquiries.",
    sections: [
      { title: "Information you choose to provide", body: "The conversation form asks for your name, email, organization or community, relevant roles, location if you choose, and the problem or pilot you want to explore." },
      { title: "How inquiry information is used", body: "GreenSwap stores the inquiry so the team can review it, route it, respond, prevent abuse, and improve the partnership process. Inquiry details are not published." },
      { title: "Essential website reliability", body: "GreenSwap sends a small, identifier-free signal when a page renders successfully or encounters a sanitized technical failure. It contains a route template, a failure category when applicable, and the website revision. It does not contain a user ID, session ID, referrer, URL query, form content, inquiry text, or item details. These signals are enabled by default to detect failures and keep the website working." },
      { title: "Optional analytics", body: "With your permission, GreenSwap records limited page, experiment, and conversion events through its first-party telemetry service and Google Analytics 4. Marketing consent separately enables aggregated Google demographic reporting. Names, email addresses, inquiry text, and item details are not sent as analytics events." },
      { title: "Service providers", body: "GreenSwap uses Microsoft Azure infrastructure, Google Analytics when consented, and may use Azure Communication Services to deliver inquiry notifications. Operational logs may contain technical request information used for security and reliability." },
      { title: "Your choices", body: "You can decline optional analytics or reopen Privacy choices from the website footer. Do not submit confidential, regulated, or sensitive personal information through the public inquiry form. To ask about an inquiry or request a privacy review, contact info@greenswap.us." },
    ],
  },
  terms: { title: "Clear expectations make better pilots.", eyebrow: "Website terms", description: "Basic terms for using the GreenSwap public website and its pilot materials.", intro: "This website explains GreenSwap's mission, available app capabilities, proposed pilots, and longer-term direction. It is informational and does not itself create a partnership agreement.", sections: [{ title: "Pilot proposals", body: "Field guides and sponsor concepts are starting points for discussion, not guarantees of availability, savings, diversion, participation, or environmental outcomes." }, { title: "Local responsibility", body: "Every handoff remains subject to applicable law, property or community rules, item eligibility, safety guidance, and the responsibilities agreed by participants and partners." }, { title: "Impact claims", body: "Illustrative methods and pilot signals are not verified outcomes. Public attribution requires documented evidence and an agreed allocation method." }, { title: "Materials", body: "GreenSwap branding and materials may be shared for a prospective GreenSwap conversation. A partner may not imply an active partnership, endorsement, or verified impact without written approval." }] },
  accessibility: { title: "GreenSwap should work for more people.", eyebrow: "Accessibility", description: "GreenSwap's accessibility commitment and how to report a barrier on the public website.", intro: "GreenSwap aims to make its public stories, partner materials, and core actions usable across devices, input methods, and assistive technologies.", sections: [{ title: "What we support", body: "The website uses semantic headings, keyboard-accessible controls, visible focus, reduced-motion support, responsive layouts, descriptive text alternatives, and print-ready documents." }, { title: "What we continue to test", body: "We continue testing real-device mobile browsers, screen readers, zoom and reflow, color contrast, form errors, PDF reading order, and third-party content." }, { title: "Report a barrier", body: "Email info@greenswap.us with the page, task, device or assistive technology, and the difficulty you encountered. GreenSwap will use the report to prioritize a practical correction." }] },
};

function TrustPage({ pageKey }: { pageKey: TrustPageKey }) {
  const page = trustPages[pageKey];
  return <main id="main" className="trust-page"><PageMeta path={`/${pageKey}`} title={`${page.eyebrow} | GreenSwap`} description={page.description} /><section className="trust-hero"><div className="shell"><p className="eyebrow eyebrow-light"><span>{page.eyebrow}</span></p><h1>{page.title}</h1><p>{page.intro}</p></div></section><section className="trust-content section"><div className="shell">{page.sections.map(({title,body})=><article key={title}><h2>{title}</h2><p>{body}</p></article>)}<div className="trust-contact"><HeartHandshake /><div><strong>Have a question?</strong><p>Contact <a href="mailto:info@greenswap.us">info@greenswap.us</a> or <a href="/connect">start a GreenSwap conversation</a>.</p></div></div></div></section></main>;
}

function NotFoundPage() {
  return <main id="main" className="not-found-page"><PageMeta title="Page not found | GreenSwap" description="The GreenSwap page you requested could not be found." robots="noindex,follow" /><div><img src="/branding/greenswap-mark.svg" alt="" /><p>That route needs another life.</p><h1>We could not find this page.</h1><span>Try the GreenSwap story, partner field guides, or the app.</span><div><a className="button button-primary" href="/">GreenSwap home</a><a className="button button-dark" href="/partners">Explore partners</a></div></div></main>;
}

function HomePage({ onOpenInquiry }: { onOpenInquiry: (role?: string) => void }) {
  return (
    <main id="main">
      <PageMeta title="GreenSwap | Set useful things free" description="GreenSwap helps every useful thing find its next life and fulfill its purpose." />
      <Hero /><HomeRecognition /><HomeHowItWorks /><HomeTransformation /><JourneyDiscovery onOpenInquiry={onOpenInquiry} /><Ecosystem onOpenInquiry={onOpenInquiry} /><ImpactPreview /><HomeConversationEntry />
    </main>
  );
}

export default function App() {
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 30, restDelta: 0.001 });
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  useEffect(() => {
    initGa4();
    trackWebsitePageView();
    recordPageViewOk();
  }, [path]);
  const openInquiry = (role?: string) => {
    const params = new URLSearchParams({ source: path });
    const resolvedRole = resolveInquiryRole(role);
    if (resolvedRole) params.set("role", resolvedRole);
    window.location.assign(`/connect?${params.toString()}`);
  };
  const campaignMatch = path.match(/^\/c\/([^/]+)(?:\/events\/([^/]+))?(\/flyer)?$/);
  const page = campaignMatch
    ? <CommunityCampaignPage slug={decodeURIComponent(campaignMatch[1])} eventId={campaignMatch[2] ? decodeURIComponent(campaignMatch[2]) : undefined} flyer={Boolean(campaignMatch[3])} />
    : path === "/communities"
      ? <CommunityDirectoryPage />
    : path === "/partners/housing"
    ? <HousingPage onOpenInquiry={openInquiry} />
    : path === "/partners/community-groups"
      ? <CommunityGroupsPage onOpenInquiry={openInquiry} />
      : audienceGuides[path]
        ? <AudienceGuidePage guide={audienceGuides[path]} onOpenInquiry={openInquiry} />
      : path === "/partners"
        ? <PartnerHub onOpenInquiry={openInquiry} />
        : path === "/start"
          ? <PublicStartPage />
        : path === "/impact"
          ? <ImpactPage />
        : path === "/sponsors"
          ? <SponsorPage onOpenInquiry={openInquiry} />
          : path === "/connect"
            ? <ConnectPage />
            : path === "/about" || path === "/privacy" || path === "/terms" || path === "/accessibility"
              ? <TrustPage pageKey={path.slice(1) as TrustPageKey} />
              : path === "/"
                ? <HomePage onOpenInquiry={openInquiry} />
                : <NotFoundPage />;

  return (
    <MotionConfig reducedMotion="user">
      <a className="skip-link" href="#main">Skip to content</a>
      <motion.div className="scroll-progress" style={{ scaleX: progress }} />
      {!campaignMatch?.[3] && <Header onOpenInquiry={openInquiry} />}
      {page}
      {!campaignMatch?.[3] && <Footer />}
      {!campaignMatch?.[3] && <ConsentPanel />}
      {path === "/" && <div className="mobile-dock"><button onClick={() => scrollToId("home-how-it-works")}><Sparkles /> How it works</button><a href={BROWSE_URL}>Explore items <ArrowRight /></a></div>}
    </MotionConfig>
  );
}
